import {create} from 'zustand';
import * as Monaco from 'monaco-editor';
import {editor} from 'monaco-editor';
import {CompilerService} from '../compiler/CompilerService';
import {LanguageService} from '../languages/languageService';
import EditorModelManager from '../EditorModelManager';
import {CanisterInfo} from '../../types';
import {buildFileTree, updateFileTreeAfterSave, updateFileTreeForFile} from '../file/fileTreeUtils';
import {pathUtils} from '../file/pathUtils';
import {getDirectoryForFile, validateFilePath, getAllowedExtensions, createFilePath} from '../pathOps';
import {Principal} from '@dfinity/principal';
import {
    Project,
    PackageInfo,
    NPMPackageInfo,
    SemanticVersion,
    ProjectVersion
} from '../../UserCanister/candid/main.did';
import {withCompilerInitialization, withLanguageServiceInitialization} from './ideMiddleware';
import {PackageJSONGenerator} from '../PackageJSONGenerator';
import {MopsTOMLGenerator} from '../MopsTOMLGenerator';
import {JSTypeDefinitionService} from '../JSTypeDefinitionService';
import {MotokoTypeDefinitionService} from '../MotokoTypeDefinitionService';
import {handleBackendDeploy, handleFrontendDeploy} from '../DeployHandler';
import {createDebouncedProjectUpdate, createSafeProjectUpdate, debounce} from './debounce';
import {ProjectFile} from "../Sidebar";


type CodeEditor = editor.IStandaloneCodeEditor;

interface FileTreeNode {
    name: string;
    path: string;
    isDirectory: boolean;
    children?: FileTreeNode[];
    originalFile?: ProjectFile;
    containsMixedFileSystems?: boolean;
    fileSystemType?: 'backend' | 'frontend' | 'asset';
}

interface AIRule {
    id: string;
    name: string;
    description: string;
    content: string;
    type: 'custom' | 'url' | 'mcp' | 'template';
    enabled: boolean;
    category: 'security' | 'style' | 'performance' | 'architecture' | 'general';
    url?: string;
    mcpServer?: string;
    lastUpdated?: number;
    status?: 'active' | 'loading' | 'error';
    errorMessage?: string;
}

interface GitHubSource {
    id: string;
    name: string;
    githubUrl: string;
    githubPath?: string;
    githubBranch?: string;
    extractionType: 'readme' | 'contributing' | 'docs' | 'examples' | 'all';
    category: 'security' | 'style' | 'performance' | 'architecture' | 'general';
    description: string;
    status: 'active' | 'loading' | 'error';
    lastFetched?: number;
    contentPreview?: string;
    errorMessage?: string;
}


// Define the IDE store state
export interface IDEState {
    // System dependencies (injected)
    userActor: any | null;

    projectSwitchProtection: {
        lastUserSwitchTime: number;
        lastUserSwitchProjectId: string | null;
        isLocked: boolean;
    };

    showWasmSelectionDialog: boolean;
    availableWasmFiles: Array<{
        path: string;
        fileName: string;
        size?: number;
        lastModified?: string;
    }>;

    oversizedWasmFiles: string[];
    showOversizedWasmWarning: boolean;
    showWasmDeletionDialog: boolean;
    availableWasmFilesForDeletion: Array<{
        path: string;
        fileName: string;
        size?: number;
        fullPath: string;
    }>;

    platformActor: any | null;
    userPrincipal: Principal | null;
    identity: any | null;

    // Core services
    monaco: typeof Monaco | null;
    compilerService: CompilerService | null;
    languageService: LanguageService | null;
    editorManager: EditorModelManager | null;
    editorRef: React.MutableRefObject<CodeEditor | undefined> | null;
    jsTypeDefinitionService: JSTypeDefinitionService | null;
    motokoTypeDefinitionService: MotokoTypeDefinitionService | null;
    isPackageUpdateForced: boolean;
    packageJsonLastSaved: number; // Timestamp of last manual package.json save

    // Editor state
    activeFile: string;
    fileContent: string;
    hasUnsavedChanges: boolean;
    files: ProjectFile[];
    fileTree: FileTreeNode | null;
    currentPath: string;

    // Project state
    project: Project | null;
    activeType: 'frontend' | 'backend';
    userCanisters: CanisterInfo[];
    setDefaultFileForActiveType: () => void;

    // Compiler state
    compilerReady: boolean;
    compilationStatus: 'idle' | 'compiling' | 'deploying';
    compilerDiagnostics: any[];
    deploymentError: string | null;
    compilerError: string | null;

    // Package management state
    motokoPackagesLastUpdated: number;
    npmPackagesLastUpdated: number;
    needsTypeDefinitionRefresh: boolean;
    isAddingPackage: boolean;
    hasRunInitialPackageUpdate: boolean;
    hasRunInitialMopsTomlUpdate: boolean;
    isConfiguringTypeScript: boolean;
    lastTypeScriptConfigSignature: string;
    cachedPackageJsonPath: string | null; // Cache package.json path to avoid file fetches
    parseMopsToml: () => Promise<void>;
    parsePackageJson: () => Promise<void>;

    // File operation state
    fileOperationLock: boolean;
    fileToDelete: string | null;
    newFileName: string;
    newFilePath: string | null;
    isDownloadingFiles: boolean;
    activeDownloadPromise: Promise<any> | null; // Share download promise across requests
    activeDownloadKey: string | null; // Cache key for active download

    // UI state
    loading: boolean;
    activeConsoleTab: 'console' | 'ai'; // <-- ADD THIS LINE
    isMotokoPackagesDialogOpen: boolean;
    isNPMPackagesDialogOpen: boolean;
    showWasmNotFoundDialog: boolean;
    showNewFileDialog: boolean;
    showNewFolderDialog: boolean;
    showDeleteConfirmation: boolean;
    showSaveConfirmation: boolean;
    showCompilerErrors: boolean;
    useCustomPath: boolean;

    // External callbacks
    onError: ((error: string | null) => void) | null;
    onSuccess: ((message: string | null) => void) | null;
    onProjectUpdate: (() => Promise<void>) | null;
    debouncedProjectUpdate: (structuralChange?: boolean, contentOnly?: boolean) => void;

    originalProjectUpdate: (() => Promise<void>) | null;


    // Actions - Initialization
    initializeMonaco: (monaco: typeof Monaco) => void;
    initializeCompiler: () => Promise<void>;
    setEditorRef: (ref: React.MutableRefObject<CodeEditor | undefined>) => void;

    // Actions - File Management
    setActiveFile: (file: string) => void;
    setFileContent: (content: string) => void;
    handleEditorChange: (content: string | undefined) => void;
    setFiles: (files: ProjectFile[]) => void;
    saveFile: (isNew?: boolean) => Promise<void>;
    createFolder: (path: string) => Promise<void>;
    deleteFile: (filePath: string) => void;
    confirmDeleteFile: () => Promise<void>;
    refreshFiles: (versionId?: string, forceRefresh?: boolean) => Promise<void>;
    handleClaudeContentUpdate: (newContent: string) => void;

    // Actions - Project Management
    setProject: (project: Project) => void;
    setActiveType: (type: 'frontend' | 'backend') => void;
    setCompilationStatus: (status: 'idle' | 'compiling' | 'deploying') => void;
    loadProjectData: (projectId: string) => Promise<void>;

    // Actions - Package Management
    addMotokoPackage: (packageInfo: PackageInfo) => Promise<string | null>;
    removeMotokoPackage: (packageName: string) => Promise<string | null>;
    addNPMPackage: (packageInfo: NPMPackageInfo) => Promise<string | null>;
    removeNPMPackage: (packageName: string) => Promise<string | null>;
    refreshTypeDefinitions: () => Promise<void>;
    savePackageJSON: (content: string, isInitialLoad?: boolean, packageJsonPath?: string) => Promise<void>;
    saveMopsToml: (content: string, isInitialLoad?: boolean) => Promise<void>;
    updateMopsTomlFile: () => Promise<void>;
    updatePackageFile: () => Promise<void>;
    configureTypeScriptDefaults: (monacoInstance: typeof Monaco, npmPackages: NPMPackageInfo[]) => Promise<void>;

    // Actions - UI Management
    toggleClaudePanel: () => void;
    toggleMotokoPackagesDialog: () => void;
    toggleNPMPackagesDialog: () => void;
    setCompilerDiagnostics: (diagnostics: any[]) => void;
    setDeploymentError: (error: string | null) => void;
    showNewFile: () => void;
    showNewFolder: () => void;
    hideDialogs: () => void;

    // Actions - Deployment
    deploy: (deploymentId?: string | null, initArgs?: any | null, installMode?: string | null, projectType?: string, targetCanisterId?: string | null, selectedActorFile?: string | null) => Promise<void>;
    downloadWasm: () => Promise<void>;
    safeProjectUpdate: () => Promise<void>;

    getProjectVersions: () => Promise<void>;
    switchToVersion: (versionId: string) => Promise<void>;
    createNewVersion: (versionData: any) => Promise<void>;
    updateVersionStatus: (versionId: string, newStatus: string) => Promise<void>;
    toggleVersioningDialog: () => void;

    showWasmSelection: () => void;
    hideWasmSelection: () => void;
    downloadSelectedWasm: (wasmFile: { path: string; fileName: string }) => Promise<void>;

    // Version state
    projectVersions: ProjectVersion[];
    currentVersionId: string | null;
    isVersioningDialogOpen: boolean;
    isLoadingVersions: boolean;

    getLatestProjectVersion: () => Promise<void>;
    initializeProjectVersion: () => Promise<void>;

    activeTab: string;
    setActiveTab: (tab: string) => void;

    isFileStructureChanged: (oldFiles: ProjectFile[], newFiles: ProjectFile[]) => boolean;
    updateFileInTree: (filePath: string, newContent: string) => {
        updatedFiles: ProjectFile[];
        updatedFileTree: FileTreeNode | null;
    };

    performOptimizedRefreshAfterDeploy: (oldFiles: ProjectFile[], versionId: string | null) => Promise<void>;


    diagnosticsTimeout: NodeJS.Timeout | null;

    previousActiveType: 'backend' | 'frontend';

    isUserInitiatedFileSwitch?: boolean;

    aiRules: AIRule[];
    setAIRules: (rules: AIRule[]) => void;
    addAIRule: (rule: Omit<AIRule, 'id' | 'lastUpdated'>) => void;
    updateAIRule: (id: string, updates: Partial<AIRule>) => void;
    deleteAIRule: (id: string) => void;
    toggleAIRule: (id: string) => void;
    getActiveAIRulesContext: () => string;
    githubSources: GitHubSource[];
    setGitHubSources: (sources: GitHubSource[]) => void;
    addGitHubSource: (source: Omit<GitHubSource, 'id'>) => void;
    updateGitHubSource: (id: string, updates: Partial<GitHubSource>) => void;
    deleteGitHubSource: (id: string) => void;
    markUserInitiatedProjectSwitch: (projectId: string) => void;

    packageOperationLock: {
        isLocked: boolean;
        operationType: string | null;
        projectId: string | null;
        timestamp: number;
    };

    showPayloadSizeErrorDialog: boolean;
    payloadSizeErrorMessage: string | null;
    handlePayloadSizeError: (error: any) => boolean;
    safeGetProjectFiles: (projectId: string, versionParam: any[], forceRefresh?: boolean) => Promise<any>;

    downloadProjectFilesBatched: (projectId: string, versionParam: any[]) => Promise<any>;
}

// Create the store with middlewares
const useIDEStore = create<IDEState>()(
    withCompilerInitialization(
        withLanguageServiceInitialization(
            (set, get) => ({

                aiRules: [],

                githubSources: [],

                previousActiveType: 'backend',

                isUserInitiatedFileSwitch: false,

                diagnosticsTimeout: null,

                isPackageUpdateForced: false,
                packageJsonLastSaved: 0,

                showWasmSelectionDialog: false,
                availableWasmFiles: [],

                oversizedWasmFiles: [],
                showOversizedWasmWarning: false,
                showWasmDeletionDialog: false,
                availableWasmFilesForDeletion: [],

                showPayloadSizeErrorDialog: false,
                payloadSizeErrorMessage: null,

                // Initialize activeTab with a default value
                activeTab: 'ai-studio',

                // Add the action to update it
                setActiveTab: (tab: string) => {
                    set({activeTab: tab});
                },

                // System dependencies (injected)
                userActor: null,
                platformActor: null,

                projectSwitchProtection: {
                    lastUserSwitchTime: 0,
                    lastUserSwitchProjectId: null,
                    isLocked: false
                },

                packageOperationLock: {
                    isLocked: false,
                    operationType: null,
                    projectId: null,
                    timestamp: 0
                },

                userPrincipal: null,
                identity: null,

                // Core services
                monaco: null,
                compilerService: new CompilerService(),
                languageService: null,
                editorManager: null,
                editorRef: null,
                jsTypeDefinitionService: null,
                motokoTypeDefinitionService: null,

                // Editor state
                activeFile: '',
                fileContent: '',
                hasUnsavedChanges: false,
                files: [],
                fileTree: null,
                currentPath: '/',

                // Project state
                project: null,
                activeType: 'backend',
                userCanisters: [],

                // Compiler state
                compilerReady: false,
                compilationStatus: 'idle',
                compilerDiagnostics: [],
                deploymentError: null,
                compilerError: null,

                // Package management state
                motokoPackagesLastUpdated: 0,
                npmPackagesLastUpdated: 0,
                needsTypeDefinitionRefresh: false,
                isConfiguringTypeScript: false,
                lastTypeScriptConfigSignature: '',
                cachedPackageJsonPath: null,
                isAddingPackage: false,
                hasRunInitialPackageUpdate: false,
                hasRunInitialMopsTomlUpdate: false,

                // File operation state
                fileOperationLock: false,
                fileToDelete: null,
                newFileName: '',
                newFilePath: null,
                useCustomPath: false,
                isDownloadingFiles: false,
                activeDownloadPromise: null,
                activeDownloadKey: null,

                // UI state
                loading: false,
                activeConsoleTab: 'console',
                isMotokoPackagesDialogOpen: false,
                isNPMPackagesDialogOpen: false,
                showWasmNotFoundDialog: false,
                showNewFileDialog: false,
                showNewFolderDialog: false,
                showDeleteConfirmation: false,
                showSaveConfirmation: false,
                showCompilerErrors: false,

                // External callbacks
                onError: null,
                onSuccess: null,
                onProjectUpdate: null,
                debouncedProjectUpdate: () => {
                },
                originalProjectUpdate: null,

                projectVersions: [],
                currentVersionId: null,
                isVersioningDialogOpen: false,
                isLoadingVersions: false,


                setAIRules: (rules: AIRule[]) => {
                    set({aiRules: rules});
                    // Persist to localStorage
                    try {
                        localStorage.setItem('ide-ai-rules', JSON.stringify(rules));
                    } catch (error) {
                        console.warn('Failed to save AI rules to localStorage:', error);
                    }
                },

                addAIRule: (ruleData) => {
                    const newRule: AIRule = {
                        ...ruleData,
                        id: `rule-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                        lastUpdated: Date.now()
                    };

                    const updatedRules = [...get().aiRules, newRule];
                    get().setAIRules(updatedRules);
                },

                updateAIRule: (id: string, updates: Partial<AIRule>) => {
                    const updatedRules = get().aiRules.map(rule =>
                        rule.id === id
                            ? {...rule, ...updates, lastUpdated: Date.now()}
                            : rule
                    );
                    get().setAIRules(updatedRules);
                },

                deleteAIRule: (id: string) => {
                    const updatedRules = get().aiRules.filter(rule => rule.id !== id);
                    get().setAIRules(updatedRules);
                },

                toggleAIRule: (id: string) => {
                    get().updateAIRule(id, {
                        enabled: !get().aiRules.find(r => r.id === id)?.enabled
                    });
                },

                getActiveAIRulesContext: (): string => {
                    const enabledRules = get().aiRules.filter(rule =>
                        rule.enabled && rule.status === 'active'
                    );

                    if (enabledRules.length === 0) return '';

                    const rulesByCategory = enabledRules.reduce((acc, rule) => {
                        if (!acc[rule.category]) acc[rule.category] = [];
                        acc[rule.category].push(rule);
                        return acc;
                    }, {} as Record<string, AIRule[]>);

                    let context = '\n\n=== AI ASSISTANT RULES ===\n';
                    context += 'Please follow these specific guidelines when providing assistance:\n\n';

                    Object.entries(rulesByCategory).forEach(([category, rules]) => {
                        context += `## ${category.toUpperCase()} GUIDELINES:\n`;
                        rules.forEach(rule => {
                            context += `- ${rule.name}: ${rule.content}\n`;
                        });
                        context += '\n';
                    });

                    context += '=== END RULES ===\n\n';
                    return context;
                },

                setGitHubSources: (sources) => set({githubSources: sources}),

                addGitHubSource: (sourceData) => {
                    const newSource: GitHubSource = {
                        ...sourceData,
                        id: `github-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
                    };
                    set((state) => ({
                        githubSources: [...state.githubSources, newSource]
                    }));
                },

                updateGitHubSource: (id, updates) => {
                    set((state) => ({
                        githubSources: state.githubSources.map(source =>
                            source.id === id ? {...source, ...updates} : source
                        )
                    }));
                },

                deleteGitHubSource: (id) => {
                    set((state) => ({
                        githubSources: state.githubSources.filter(source => source.id !== id)
                    }));
                },

                refreshFiles: async (versionId = null, forceRefresh = false) => {
                    const state = get();
                    const startingProjectId = state.project?.id; // Capture project ID at start

                    if (!state.project || state.project.id === 'none' || !state.userActor || !state.userPrincipal) {
                        console.log('[refreshFiles] Missing required dependencies, aborting');
                        return;
                    }

                    // Determine effective version ID
                    const effectiveVersionId = versionId !== undefined ? versionId : state.currentVersionId;
                    
                    // Simple debounce: skip if we're already loading
                    if (state.loading && !forceRefresh) {
                        console.log('[refreshFiles] Already loading, skipping duplicate refresh');
                        return;
                    }

                    console.log(`[refreshFiles] Starting refresh for project ${startingProjectId} with version: ${effectiveVersionId || 'current'}`);

                    try {
                        // Only show loading for large projects or if explicitly needed
                        const fileCount = state.files?.length || 0;
                        if (fileCount > 50) {
                            set({loading: true});
                        }

                        // Skip delay for faster refresh - not needed for state settling

                        // Check if project changed during the delay
                        if (get().project?.id !== startingProjectId) {
                            console.log('[refreshFiles] Project changed during initialization, aborting');
                            return;
                        }

                        // Version parameter for Candid
                        const versionParam = effectiveVersionId ? [effectiveVersionId] : [];
                        
                        console.log(`[refreshFiles] Fetching files for project ${startingProjectId} with versionId: ${effectiveVersionId || 'current'}`);

                        // Fetch the files from the backend
                        const filesResult = await get().safeGetProjectFiles(
                            state.project.id,
                            versionParam,
                            forceRefresh // Pass forceRefresh flag to bypass cache
                        );


                        if ('ok' in filesResult) {
                            console.log('[DEBUG BACKEND] Raw backend response:', filesResult.ok.map(f => ({
                                id: f.id,
                                path: f.path,
                                fileName: f.fileName,
                                fullPath: f.fullPath
                            })));
                        }

                        // Check if project changed during the API call
                        if (get().project?.id !== startingProjectId) {
                            console.log('[refreshFiles] Project changed during API call, aborting');
                            return;
                        }

                        if ('ok' in filesResult) {
                            // Transform files to our internal format while preserving original backend paths
                            let transformedFiles = enhancedTransformFiles(filesResult.ok);

                            console.log(`[refreshFiles] Transformed ${filesResult.ok.length} files to ${transformedFiles.length} valid files for project ${startingProjectId}`);

                            // Check if project changed during file transformation
                            if (get().project?.id !== startingProjectId) {
                                console.log('[refreshFiles] Project changed during file transformation, aborting');
                                return;
                            }

                            // CRITICAL: Only preserve content for the currently active file if it has unsaved changes
                            // Saved files should use backend content, not stale local content
                            const currentState = get();
                            const activeFileName = currentState.activeFile;
                            const hasUnsavedChanges = currentState.hasUnsavedChanges;
                            
                            // Only preserve content if:
                            // 1. There's an active file
                            // 2. It has unsaved changes (hasUnsavedChanges flag is true)
                            // 3. The fileContent is different from what's in the backend response
                            if (activeFileName && hasUnsavedChanges) {
                                const activeFileIndex = transformedFiles.findIndex(f => f.name === activeFileName);
                                if (activeFileIndex >= 0) {
                                    const backendContent = transformedFiles[activeFileIndex].content;
                                    const localContent = currentState.fileContent;
                                    
                                    // Only preserve if local content is actually different from backend
                                    if (localContent !== undefined && localContent !== backendContent) {
                                        transformedFiles[activeFileIndex] = {
                                            ...transformedFiles[activeFileIndex],
                                            content: localContent
                                        };
                                        console.log(`[refreshFiles] Preserved unsaved content for active file: ${activeFileName} (local differs from backend)`);
                                    } else {
                                        console.log(`[refreshFiles] Active file ${activeFileName} has unsaved flag but content matches backend - using backend content`);
                                    }
                                }
                            } else {
                                console.log(`[refreshFiles] Not preserving content - activeFile: ${activeFileName}, hasUnsavedChanges: ${hasUnsavedChanges}`);
                            }

                            // Current file paths for comparison
                            const currentFiles = state.files;

                            // Force rebuild if version changed or if we don't have a tree yet
                            const forceRebuild = effectiveVersionId !== state.currentVersionId || !state.fileTree;

                            // Check if structure changed
                            const structureChanged = forceRebuild || get().isFileStructureChanged(currentFiles, transformedFiles);

                            if (structureChanged) {
                                console.log(`[refreshFiles] Rebuilding file tree for project ${startingProjectId} due to ${forceRebuild ? 'version change' : 'structure change'}`);

                                try {
                                    // Build a new file tree
                                    const fileTree = buildFileTree(transformedFiles);

                                    // Final check before updating state
                                    if (get().project?.id !== startingProjectId) {
                                        console.log('[refreshFiles] Project changed during tree building, aborting state update');
                                        return;
                                    }

                                    set({
                                        files: transformedFiles,
                                        fileTree: fileTree,
                                        loading: false,
                                        currentVersionId: effectiveVersionId
                                    });

                                    console.log(`[refreshFiles] Successfully updated state with new tree for project ${startingProjectId}`);
                                } catch (treeError) {
                                    console.error("[refreshFiles] Failed to build file tree:", treeError);

                                    // Check again before fallback update
                                    if (get().project?.id !== startingProjectId) {
                                        console.log('[refreshFiles] Project changed during tree error handling, aborting');
                                        return;
                                    }

                                    // Fallback: update without tree
                                    set({
                                        files: transformedFiles,
                                        loading: false,
                                        currentVersionId: effectiveVersionId
                                    });
                                }
                            } else {
                                // Structure didn't change, just update file contents
                                console.log(`[refreshFiles] Updating file contents without rebuilding tree for project ${startingProjectId}`);

                                // Final check before content update
                                if (get().project?.id !== startingProjectId) {
                                    console.log('[refreshFiles] Project changed before content update, aborting');
                                    return;
                                }

                                // Maintain the same file tree reference
                                set({
                                    files: transformedFiles,
                                    loading: false,
                                    currentVersionId: effectiveVersionId
                                });
                            }

                            // Handle active file preservation/selection
                            const currentActiveFile = state.activeFile;
                            if (currentActiveFile) {
                                console.log(`[refreshFiles] Checking if current active file still exists: ${currentActiveFile}`);

                                // Check if the current file still exists in the refreshed files - use exact path comparison
                                const fileStillExists = transformedFiles.some(file => file.name === currentActiveFile);

                                if (fileStillExists) {
                                    // File still exists, keep it active and just update content
                                    console.log(`[refreshFiles] Preserving active file after refresh: ${currentActiveFile}`);
                                    const fileData = transformedFiles.find(file => file.name === currentActiveFile);

                                    // Final check before setting active file
                                    if (get().project?.id !== startingProjectId) {
                                        console.log('[refreshFiles] Project changed during active file preservation, aborting');
                                        return;
                                    }

                                    if (fileData) {
                                        set({
                                            activeFile: currentActiveFile,
                                            fileContent: typeof fileData.content === 'string' ? fileData.content : '',
                                            hasUnsavedChanges: false
                                        });
                                    }
                                } else {
                                    // File no longer exists, set a default file after a delay
                                    console.log(`[refreshFiles] Active file no longer exists after refresh: ${currentActiveFile}, will set default`);
                                    setTimeout(() => {
                                        // Check one more time before setting default
                                        if (get().project?.id === startingProjectId) {
                                            get().setDefaultFileForActiveType();
                                        }
                                    }, 100);
                                }
                            } else {
                                // No active file, set a default file after a delay
                                console.log(`[refreshFiles] No active file, will set default for project ${startingProjectId}`);
                                setTimeout(() => {
                                    // Check one more time before setting default
                                    if (get().project?.id === startingProjectId) {
                                        get().setDefaultFileForActiveType();
                                    }
                                }, 100);
                            }

                            console.log(`[refreshFiles] Successfully completed refresh for project ${startingProjectId}`);

                        } else {
                            console.error("[refreshFiles] Failed to load files:", filesResult.err);
                            if (state.onError) {
                                state.onError(`Failed to load files: ${filesResult.err}`);
                            }

                            // Only update state if we're still on the same project
                            if (get().project?.id === startingProjectId) {
                                set({
                                    files: [],
                                    fileTree: null,
                                    activeFile: '',
                                    fileContent: '',
                                    loading: false
                                });
                            }
                        }
                    } catch (error) {
                        console.error("[refreshFiles] Error refreshing files:", error);

                        if (state.onError) {
                            const errorMessage = error instanceof Error
                                ? error.message
                                : String(error);
                            state.onError(`Error refreshing files: ${errorMessage}`);
                        }

                        // Only update error state if we're still on the same project
                        if (get().project?.id === startingProjectId) {
                            set({
                                files: [],
                                fileTree: null,
                                activeFile: '',
                                fileContent: '',
                                loading: false
                            });
                        }
                    } finally {
                        // Only clear loading state if we're still on the same project
                        if (get().project?.id === startingProjectId) {
                            set({loading: false});
                        }
                    }
                },


                switchToVersion: async (versionId) => {
                    const state = get();
                    const currentProjectId = state.project?.id;

                    if (!currentProjectId || currentProjectId === 'none') {
                        console.error("[ERROR] Cannot switch version - no active project");
                        return;
                    }

                    // Special case: Handle "current" string as meaning "no version"
                    if (versionId === "current") {
                        console.log("[INFO] Switching to current files mode via switchToVersion");

                        // Remember the current active file path
                        const previousActiveFile = state.activeFile;
                        const previousContent = state.fileContent;

                        // Update version ID first without triggering a full reload
                        set({
                            currentVersionId: null,
                            // Don't set loading: true here - that's causing the editor reload
                            // Don't clear activeFile or fileContent yet
                        });

                        // Fetch files for current version
                        const filesResult = await get().safeGetProjectFiles(
                            state.project.id,
                            []
                        );

                        if ('ok' in filesResult) {
                            // Transform files to our internal format
                            const transformedFiles = enhancedTransformFiles(filesResult.ok);

                            // Check if file structure changed
                            const structureChanged = state.isFileStructureChanged(state.files, transformedFiles);

                            // Only rebuild tree if necessary
                            if (structureChanged || !state.fileTree) {
                                const newFileTree = buildFileTree(transformedFiles);
                                set({
                                    files: transformedFiles,
                                    fileTree: newFileTree,
                                    // Don't set loading here
                                });
                            } else {
                                // Keep the existing tree reference
                                set({
                                    files: transformedFiles
                                    // Don't set loading here
                                });
                            }

                            // Try to set the same active file after refresh if it exists in new version
                            if (previousActiveFile) {
                                const fileExists = transformedFiles.some(f => f.name === previousActiveFile);
                                if (fileExists) {
                                    const fileData = transformedFiles.find(f => f.name === previousActiveFile);
                                    const newContent = fileData ? fileData.content : '';

                                    // Only update content if it changed
                                    if (newContent !== previousContent) {
                                        set({
                                            fileContent: newContent
                                        });
                                    }
                                } else {
                                    // File doesn't exist, select a default file
                                    get().setDefaultFileForActiveType();
                                }
                            } else {
                                // No previous file, use setDefaultFileForActiveType
                                get().setDefaultFileForActiveType();
                            }

                            if (state.onSuccess) state.onSuccess(`Switched to current working files`);
                        } else {
                            // Handle error
                            if (state.onError) state.onError(`Failed to load current files: ${filesResult.err}`);
                        }

                        return;
                    }

                    // Another check for null to handle direct null value
                    if (versionId === null) {
                        console.log("[INFO] Switching to current files mode (null parameter)");
                        const previousActiveFile = state.activeFile;
                        const previousContent = state.fileContent;

                        // Update version ID first without triggering a full reload
                        set({
                            currentVersionId: null,
                            // Don't set loading: true or clear activeFile/fileContent
                        });

                        // Fetch files for current version
                        const filesResult = await get().safeGetProjectFiles(
                            state.project.id,
                            []
                        );

                        if ('ok' in filesResult) {
                            // Transform files to our internal format
                            const transformedFiles = enhancedTransformFiles(filesResult.ok);

                            // Check if file structure changed
                            const structureChanged = state.isFileStructureChanged(state.files, transformedFiles);

                            // Only rebuild tree if necessary
                            if (structureChanged || !state.fileTree) {
                                const newFileTree = buildFileTree(transformedFiles);
                                set({
                                    files: transformedFiles,
                                    fileTree: newFileTree,
                                });
                            } else {
                                // Keep the existing tree reference
                                set({
                                    files: transformedFiles
                                });
                            }

                            // Try to restore previous active file
                            if (previousActiveFile) {
                                const fileExists = transformedFiles.some(f => f.name === previousActiveFile);
                                if (fileExists) {
                                    const fileData = transformedFiles.find(f => f.name === previousActiveFile);
                                    const newContent = fileData ? fileData.content : '';

                                    // Only update content if it changed
                                    if (newContent !== previousContent) {
                                        set({
                                            fileContent: newContent
                                        });
                                    }
                                } else {
                                    // Use setDefaultFileForActiveType to select appropriate default file
                                    get().setDefaultFileForActiveType();
                                }
                            } else {
                                // No previous file, use setDefaultFileForActiveType
                                get().setDefaultFileForActiveType();
                            }

                            if (state.onSuccess) state.onSuccess(`Switched to current working files`);
                        } else {
                            // Handle error
                            if (state.onError) state.onError(`Failed to load current files: ${filesResult.err}`);
                        }

                        return;
                    }

                    if (!versionId || !state.userActor) {
                        return;
                    }

                    try {
                        // Verify this version belongs to the current project
                        const projectVersions = get().projectVersions;
                        const versionBelongsToProject = projectVersions.some(v => v.id === versionId);

                        if (!versionBelongsToProject) {
                            console.error(`[ERROR] Version ${versionId} does not belong to current project ${currentProjectId}`);
                            if (state.onError) state.onError(`Cannot switch to version - it belongs to a different project`);
                            return;
                        }

                        // Remember current active file and content before switching
                        const previousActiveFile = state.activeFile;
                        const previousContent = state.fileContent;

                        // Set version ID first without triggering full reload
                        set({
                            currentVersionId: versionId,
                            // Don't set loading: true or clear activeFile/fileContent yet
                            isLoadingVersions: true
                        });

                        console.log(`[INFO] Fetching version details for: ${versionId}`);

                        // Get the version details to confirm it exists
                        const result = await state.userActor.getProjectVersion(versionId);

                        if ('ok' in result) {
                            // Successfully switched to version
                            console.log(`[INFO] Successfully switched to version: ${versionId}`);

                            // Fetch files for this version
                            const filesResult = await get().safeGetProjectFiles(
                                state.project.id,
                                [versionId]
                            );

                            if ('ok' in filesResult) {
                                // Transform files to our internal format
                                const transformedFiles = enhancedTransformFiles(filesResult.ok);

                                // Check if file structure changed
                                const structureChanged = state.isFileStructureChanged(state.files, transformedFiles);

                                // Only rebuild tree if necessary
                                if (structureChanged || !state.fileTree) {
                                    const newFileTree = buildFileTree(transformedFiles);
                                    set({
                                        files: transformedFiles,
                                        fileTree: newFileTree
                                    });
                                } else {
                                    // Keep the existing tree reference
                                    set({
                                        files: transformedFiles
                                    });
                                }

                                // Try to set the same active file after refresh if it exists in new version
                                if (previousActiveFile) {
                                    const fileExists = transformedFiles.some(f => f.name === previousActiveFile);
                                    if (fileExists) {
                                        const fileData = transformedFiles.find(f => f.name === previousActiveFile);
                                        const newContent = fileData ? fileData.content : '';

                                        set({
                                            activeFile: previousActiveFile,
                                            fileContent: newContent,
                                            hasUnsavedChanges: false
                                        });
                                    } else {
                                        // File doesn't exist, select a default file
                                        get().setDefaultFileForActiveType();
                                    }
                                } else {
                                    // No previous file, use setDefaultFileForActiveType
                                    get().setDefaultFileForActiveType();
                                }
                            } else {
                                if (state.onError) state.onError(`Failed to load version files: ${filesResult.err}`);
                            }

                            // Format the version for display
                            const formatVersion = (version: any) => {
                                if (!version) return "Unknown";
                                let result = `${version.major}.${version.minor}.${version.patch}`;
                                if (version.prerelease) result += `-${version.prerelease}`;
                                if (version.build) result += `+${version.build}`;
                                return result;
                            };

                            if (state.onSuccess) state.onSuccess(`Switched to version ${formatVersion(result.ok.semanticVersion)}`);
                        } else {
                            if (state.onError) state.onError(`Failed to switch version: ${result.err}`);
                            console.error("[ERROR] Failed to switch version:", result.err);

                            // Reset to current version if switching fails
                            if (state.currentVersionId !== versionId) {
                                // Use the optimized approach even for reset
                                const currentVersionId = state.currentVersionId;
                                const versionParam = currentVersionId ? [currentVersionId] : [];

                                const filesResult = await get().safeGetProjectFiles(
                                    state.project.id,
                                    versionParam
                                );

                                if ('ok' in filesResult) {
                                    const transformedFiles = enhancedTransformFiles(filesResult.ok);
                                    const structureChanged = state.isFileStructureChanged(state.files, transformedFiles);

                                    if (structureChanged || !state.fileTree) {
                                        const newFileTree = buildFileTree(transformedFiles);
                                        set({
                                            files: transformedFiles,
                                            fileTree: newFileTree
                                        });
                                    } else {
                                        set({
                                            files: transformedFiles
                                        });
                                    }

                                    // Try to find a default file
                                    get().setDefaultFileForActiveType();
                                }
                            } else {
                                // If we were already on this version but it failed, reset to no version
                                set({currentVersionId: null});

                                const filesResult = await get().safeGetProjectFiles(
                                    state.project.id,
                                    []
                                );

                                if ('ok' in filesResult) {
                                    const transformedFiles = enhancedTransformFiles(filesResult.ok);
                                    const structureChanged = state.isFileStructureChanged(state.files, transformedFiles);

                                    if (structureChanged || !state.fileTree) {
                                        const newFileTree = buildFileTree(transformedFiles);
                                        set({
                                            files: transformedFiles,
                                            fileTree: newFileTree
                                        });
                                    } else {
                                        set({
                                            files: transformedFiles
                                        });
                                    }

                                    // Try to find a default file
                                    get().setDefaultFileForActiveType();
                                }
                            }
                        }
                    } catch (error) {
                        if (state.onError) {
                            const errorMessage = error instanceof Error
                                ? error.message
                                : String(error);
                            state.onError(`Error switching version: ${errorMessage}`);
                        }
                        console.error("[ERROR] Error switching version:", error);
                    } finally {
                        set({isLoadingVersions: false});
                    }
                },

                createNewVersion: async (versionData) => {
                    const state = get();

                    if (!state.project || state.project.id === 'none' || !state.userActor) {
                        return;
                    }

                    try {
                        set({isLoadingVersions: true});

                        // Call the backend to create a new version
                        const result = await state.userActor.createProjectVersion(
                            state.project.id,
                            versionData.major,
                            versionData.minor,
                            versionData.patch,
                            versionData.prerelease ? [versionData.prerelease] : [], // Pass as array for optional
                            versionData.build ? [versionData.build] : [],           // Pass as array for optional
                            versionData.description ? [versionData.description] : [], // Pass as array for optional
                            versionData.releaseNotes ? [versionData.releaseNotes] : [], // Pass as array for optional
                            versionData.parentVersionId ? [versionData.parentVersionId] : [] // Pass as array for optional
                        );


                        if ('ok' in result) {
                            // Successfully created version
                            if (state.onSuccess) state.onSuccess(`Created new version ${formatVersion(result.ok.semanticVersion)}`);

                            // Refresh versions and switch to the new one
                            await state.getProjectVersions();
                            await state.switchToVersion(result.ok.id);
                        } else {
                            if (state.onError) state.onError(`Failed to create version: ${result.err}`);
                            console.error("[ERROR] Failed to create version:", result.err);
                        }
                    } catch (error) {
                        if (state.onError) {
                            const errorMessage = error instanceof Error
                                ? error.message
                                : String(error);
                            state.onError(`Error creating version status: ${errorMessage}`);
                        }
                        console.error("[ERROR] Error creating version:", error);
                    } finally {
                        set({isLoadingVersions: false});
                    }
                },

                updateVersionStatus: async (versionId, newStatus) => {
                    const state = get();

                    if (!versionId || !state.userActor) {
                        return;
                    }

                    try {
                        set({isLoadingVersions: true});

                        // Call the backend to update version status
                        const result = await state.userActor.updateVersionStatus(versionId, newStatus);

                        if ('ok' in result) {
                            // Successfully updated version status
                            if (state.onSuccess) state.onSuccess(`Updated version status to ${newStatus}`);

                            // Refresh versions to get updated info
                            await state.getProjectVersions();
                        } else {
                            if (state.onError) state.onError(`Failed to update version status: ${result.err}`);
                            console.error("[ERROR] Failed to update version status:", result.err);
                        }
                    } catch (error) {
                        if (state.onError) {
                            const errorMessage = error instanceof Error
                                ? error.message
                                : String(error);
                            state.onError(`Error updating version status: ${errorMessage}`);
                        }
                        console.error("[ERROR] Error updating version status:", error);
                    } finally {
                        set({isLoadingVersions: false});
                    }
                },

                toggleVersioningDialog: () => {
                    set(state => ({isVersioningDialogOpen: !state.isVersioningDialogOpen}));
                },

                initializeMonaco: (monaco) => {
                    console.log('[IDEStore] Initializing Monaco');
                    set({monaco});

                    // Create editor manager if Monaco is initialized
                    if (monaco) {
                        const manager = new EditorModelManager(monaco);
                        manager.initialize().then(() => {
                            console.log('[IDEStore] EditorModelManager initialized');
                            set({editorManager: manager});
                        });
                    }
                },

                initializeCompiler: async () => {
                    console.log('[IDEStore] Initializing compiler');
                    const {compilerService} = get();

                    if (!compilerService) {
                        console.error('[IDEStore] CompilerService is not available');
                        return;
                    }

                    try {
                        await compilerService.init();
                        console.log('[IDEStore] Compiler initialized successfully');
                        set({compilerReady: true});
                    } catch (error) {
                        console.error('[IDEStore] Compiler initialization failed:', error);
                        set({compilerError: error instanceof Error ? error.message : String(error)});
                    }
                },

                setEditorRef: (ref) => {
                    set({editorRef: ref});

                    // Initialize languageService if all dependencies are ready
                    const {monaco, compilerService, project, files} = get();
                    if (monaco && ref.current && compilerService && project && files.length > 0) {
                        console.log('[IDEStore] Creating language service');
                        const languageService = new LanguageService(
                            monaco,
                            ref.current,
                            compilerService,
                            project,
                            files
                        );
                        set({languageService});
                    }
                },

                setActiveFile: (file) => {
                    const state = get();
                    const currentFile = state.activeFile;
                    const startingProjectId = state.project?.id;

                    // Debug logging to track file switching
                    console.log(`[DEBUG] setActiveFile called with: ${file}, current: ${currentFile}`);

                    // Skip if same file or operation is locked
                    if (state.fileOperationLock || state.activeFile === file) return;

                    // Get the call stack to determine the source of this call
                    const stack = new Error().stack || '';

                    // IMPORTANT: Never allow diagnostics to change the active file
                    if (stack.includes('handleDiagnostics') ||
                        stack.includes('runDiagnostics') ||
                        stack.includes('diagnostics')) {
                        console.log(`[DEBUG] Blocked diagnostics-triggered file switch to ${file}`);
                        return;
                    }

                    // Block automated switches away from files with unsaved changes
                    // Only user-initiated actions (clicks) should be able to switch away from unsaved files
                    if (currentFile && currentFile !== file && state.hasUnsavedChanges) {
                        // Look for evidence this is a user-initiated action
                        const isUserAction = stack.includes('FileTreeView') ||
                            stack.includes('onFileClick') ||
                            stack.includes('setActiveType') ||
                            stack.includes('onClick');

                        // If not clearly user-initiated, block the switch
                        if (!isUserAction) {
                            console.log(`[DEBUG] Blocked automatic switch from file with unsaved changes: ${currentFile}`);
                            return;
                        }
                    }

                    // Lock file operations
                    set({fileOperationLock: true});

                    // Only preserve content if there are unsaved changes
                    // This prevents stale content from being preserved unnecessarily
                    if (currentFile && currentFile !== file && state.fileContent !== undefined && state.hasUnsavedChanges) {
                        const currentFileData = state.files.find(f => f.name === currentFile);
                        if (currentFileData) {
                            // Update the files array with current editor content before switching
                            const updatedFiles = state.files.map(f =>
                                f.name === currentFile ? {...f, content: state.fileContent} : f
                            );
                            set({ files: updatedFiles });
                            console.log(`[setActiveFile] Preserved unsaved content for ${currentFile} before switching to ${file}`);
                        }
                    }

                    // Clear any pending diagnostics timeout
                    if (state.diagnosticsTimeout) {
                        clearTimeout(state.diagnosticsTimeout);
                    }

                    try {
                        const fileData = state.files.find(f => f.name === file);

                        if (fileData) {

                            if (get().project?.id !== startingProjectId) {
                                console.log('[setActiveFile] Project changed during execution, aborting');
                                set({fileOperationLock: false}); // Release lock
                                return;
                            }

                            // Get the parent path just for UI purposes
                            const parentPath = pathUtils.getParentPath(file);

                            // Use the content directly without manipulation
                            let fileContent = "";

                            // Handle different content formats safely
                            if (typeof fileData.content === 'string') {
                                // Simple string content
                                fileContent = fileData.content;
                            } else if (fileData.content) {
                                // Handle array content
                                const content = fileData.content as any; // Type assertion for complex content

                                if (Array.isArray(content) && content.length > 0) {
                                    const contentItem = content[0];
                                    // Check for Text property
                                    if (contentItem && typeof contentItem === 'object' && 'Text' in contentItem) {
                                        fileContent = contentItem.Text;
                                    }
                                }
                                // Handle direct object content
                                else if (typeof content === 'object' && content !== null) {
                                    if ('Text' in content) {
                                        fileContent = content.Text;
                                    }
                                }
                            }

                            // Update state - no path manipulation
                            set({
                                activeFile: file,
                                fileContent: fileContent,
                                currentPath: parentPath, // Just for UI, not used in operations
                                hasUnsavedChanges: false
                            });

                            console.log(`[IDEStore] Set active file content, length: ${fileContent.length}`);
                        } else {
                            console.warn(`[IDEStore] File not found in current file list: ${file}`);
                        }
                    } finally {
                        // Release the lock
                        set({
                            fileOperationLock: false,
                            diagnosticsTimeout: null  // Reset the timeout reference
                        });
                    }
                },

                setFileContent: (content) => {
                    set({fileContent: content});
                },

                setActiveType: (type) => {
                    const prevType = get().activeType;
                    const currentActiveFile = get().activeFile;

                    // First set the activeType
                    set({activeType: type});

                    // IMPORTANT: Only call setDefaultFileForActiveType when:
                    // 1. We're switching between backend and frontend modes, AND
                    // 2. Either we have no active file, OR
                    // 3. The current file belongs to the previous type (we need to switch files)

                    if (prevType !== type) {
                        console.log(`[DEBUG] Mode switched from ${prevType} to ${type}, checking if file switch needed`);

                        // Check if current file matches the new active type
                        let needToSwitchFile = !currentActiveFile; // Always switch if no file is active

                        if (currentActiveFile) {
                            const isMotokoFile = currentActiveFile.endsWith('.mo');
                            const isWebFile = currentActiveFile.endsWith('.ts') ||
                                currentActiveFile.endsWith('.tsx') ||
                                currentActiveFile.endsWith('.js') ||
                                currentActiveFile.endsWith('.jsx') ||
                                currentActiveFile.endsWith('.html') ||
                                currentActiveFile.endsWith('.css');

                            // We need to switch if:
                            // - We're now in backend mode but current file is a web file
                            // - We're now in frontend mode but current file is a Motoko file
                            if ((type === 'backend' && isWebFile) || (type === 'frontend' && isMotokoFile)) {
                                console.log(`[DEBUG] Current file ${currentActiveFile} doesn't match new mode ${type}, will switch file`);
                                needToSwitchFile = true;
                            } else {
                                console.log(`[DEBUG] Current file ${currentActiveFile} is compatible with new mode ${type}, keeping it active`);
                            }
                        }

                        if (needToSwitchFile) {
                            setTimeout(() => {
                                get().setDefaultFileForActiveType();
                            }, 50);  // Small timeout to ensure state is updated
                        }
                    }

                    // If we're switching from backend to frontend, we need to ensure proper TS initialization
                    if (prevType === 'backend' && type === 'frontend') {
                        console.log('[DEBUG] Switching from backend to frontend - scheduling languageService re-initialization');

                        // Set TypeScript diagnostics to be more lenient immediately
                        const {monaco} = get();
                        if (monaco?.languages?.typescript) {
                            // Set lenient diagnostic options
                            const diagnosticsOptions = {
                                noSemanticValidation: false,
                                noSyntaxValidation: false,
                                noSuggestionDiagnostics: false,
                                diagnosticCodesToIgnore: [2307], // Ignore module not found errors
                                reportMissingImports: {severity: "warning"},
                                reportUnusedLabels: {severity: "warning"}
                            };

                            monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
                                diagnosticCodesToIgnore: [2347]
                            });
                            monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
                                diagnosticCodesToIgnore: [2347]
                            });
                        }

                        // Schedule a language service re-initialization
                        setTimeout(async () => {
                            const {monaco, editorRef, project, files, compilerService} = get();
                            console.log('[DEBUG] Re-initializing languageService for frontend mode', {
                                hasMonaco: !!monaco,
                                hasEditor: !!editorRef?.current,
                                hasProject: !!project,
                                fileCount: files.length,
                                hasCompiler: !!compilerService
                            });

                            if (monaco && editorRef?.current && project && files.length > 0 && compilerService) {
                                try {
                                    // Get the current languageService to dispose
                                    const currentLangService = get().languageService;
                                    if (currentLangService) {
                                        console.log('[DEBUG] Disposing existing languageService');
                                        currentLangService.dispose();
                                    }

                                    // Create a fresh language service
                                    console.log('[DEBUG] Creating new languageService instance');
                                    const newLanguageService = new LanguageService(
                                        monaco,
                                        editorRef.current,
                                        compilerService,
                                        project,
                                        files
                                    );

                                    // Update the store with the new service
                                    set({languageService: newLanguageService});

                                    // Apply lenient typescript settings again after new language service
                                    if (monaco.languages.typescript) {
                                        const diagnosticsOptions = {
                                            noSemanticValidation: false,
                                            noSyntaxValidation: false,
                                            noSuggestionDiagnostics: false,
                                            diagnosticCodesToIgnore: [2307],
                                            reportMissingImports: {severity: "warning"},
                                            reportUnusedLabels: {severity: "warning"}
                                        };

                                        monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions(diagnosticsOptions);
                                    }

                                    // Run diagnostics on the current file
                                    const currentFile = get().activeFile;
                                    const content = get().fileContent;
                                    if (currentFile) {
                                        console.log('[DEBUG] Running initial diagnostics on:', currentFile);
                                        await newLanguageService.handleDiagnostics(currentFile, content);

                                        // Clear any remaining TypeScript error markers and replace with warnings
                                        if (editorRef.current) {
                                            const model = editorRef.current.getModel();
                                            if (model) {
                                                monaco.editor.setModelMarkers(model, 'typescript-diagnostics', []);
                                                monaco.editor.setModelMarkers(model, 'typescript', []);
                                                monaco.editor.setModelMarkers(model, 'ts', []);
                                            }
                                        }
                                    }
                                } catch (error) {
                                    console.error('[DEBUG] Error re-initializing languageService:', error);
                                }
                            } else {
                                console.warn('[DEBUG] Cannot re-initialize languageService - missing dependencies');
                            }
                        }, 500);
                    }
                },

                handleEditorChange: (newContent) => {
                    const content = newContent || "";
                    const {activeFile, files, fileTree} = get();

                    // Log for debugging
                    console.log(`[DEBUG] handleEditorChange for file: ${activeFile}`);

                    // Check if content has changed from original
                    const originalFile = files.find(f => f.name === activeFile);
                    const hasChanges = originalFile && content !== originalFile.content;

                    // Update just the content in the files array without rebuilding the tree
                    if (originalFile) {
                        const updatedFiles = files.map(file =>
                            file.name === activeFile ? {...file, content: content} : file
                        );

                        set({
                            files: updatedFiles,
                            fileContent: content,
                            hasUnsavedChanges: hasChanges
                            // IMPORTANT: Don't set activeFile here to prevent file switching
                        });
                    } else {
                        // If file not found in array (rare case), just update the content in state
                        set({
                            fileContent: content,
                            hasUnsavedChanges: hasChanges
                        });
                    }

                    // Skip diagnostics for certain file types
                    const skipDiagnostics = ['.did', '.json', '.md', '.txt'].some(ext => activeFile.endsWith(ext));
                    if (skipDiagnostics) {
                        console.log(`[DEBUG] Skipping diagnostics for file type: ${activeFile}`);
                        return;
                    }

                    // Trigger diagnostics if language service is available
                    const languageService = get().languageService;
                    const editorRef = get().editorRef;
                    const {diagnosticsTimeout} = get();

                    if (languageService && activeFile && editorRef?.current) {
                        // Clear previous timeout if it exists
                        if (diagnosticsTimeout) {
                            clearTimeout(diagnosticsTimeout);
                        }

                        // Set a new timeout for debouncing
                        const newTimeout = setTimeout(() => {
                            try {
                                // IMPORTANT: Check that the file hasn't changed during the timeout
                                const currentActiveFile = get().activeFile;
                                if (currentActiveFile === activeFile) {
                                    console.log(`[DEBUG] Running diagnostics on content change for ${activeFile}`);
                                    languageService.handleDiagnostics(activeFile, content);
                                } else {
                                    console.log(`[DEBUG] File changed during timeout from ${activeFile} to ${currentActiveFile}, skipping diagnostics`);
                                }
                            } catch (error) {
                                console.error('[IDEStore] Error running diagnostics on content change:', error);
                            }
                        }, 300);

                        // Update the timeout in the state
                        set({diagnosticsTimeout: newTimeout});
                    }
                },

                setFiles: (files) => {
                    const currentProject = get().project;

                    // Only update if we have a valid project
                    if (!currentProject || currentProject.id === 'none') {
                        console.log('[setFiles] No valid project, skipping file tree update');
                        return;
                    }

                    console.log(`[setFiles] Updating files and tree for project: ${currentProject.id}`);
                    const fileTree = buildFileTree(files);
                    set({files, fileTree});
                },

                saveFile: async (isNew = false) => {
                    const state = get();
                    const {
                        userActor,
                        userPrincipal,
                        activeFile,
                        fileContent,
                        project,
                        newFileName,
                        newFilePath,
                        useCustomPath,
                        activeType,
                        files,
                        fileTree,
                        onSuccess,
                        onError,
                        currentVersionId
                    } = state;

                    if (!userPrincipal || !userActor || !project?.id || !project.name) {
                        console.error('[IDEStore] Missing required dependencies for save operation');
                        return;
                    }

                    // Log the operation details for debugging
                    console.log('[IDEStore] Save operation:', {
                        isNew,
                        newFileName,
                        newFilePath,
                        useCustomPath,
                        activeFile,
                        projectId: project.id,
                        projectName: project.name,
                        activeType,
                        currentVersionId
                    });

                    try {
                        // Only set loading for new files, not for existing files
                        if (isNew) {
                            set({loading: true});
                        }

                        // Format version parameter for Candid
                        const versionParam = currentVersionId ? [currentVersionId] : [];

                        if (isNew) {
                            // NEW FILE CREATION - minimal path handling needed

                            // Validate that we have a filename
                            if (!newFileName || newFileName.trim() === '') {
                                throw new Error('Filename is required');
                            }

                            // Get just the filename without any path - minimal extraction needed
                            const fileName = newFileName.includes('/')
                                ? newFileName.substring(newFileName.lastIndexOf('/') + 1)
                                : newFileName;

                            // Validate file type
                            if (!validateFilePath(fileName, activeType, project.name)) {
                                const extensions = getAllowedExtensions(activeType);
                                throw new Error(
                                    `Invalid file type. ${activeType} files must use one of these extensions: ${extensions.join(', ')}`
                                );
                            }

                            // Use the selected directory path or fall back to default
                            const targetDir = newFilePath || getDirectoryForFile(fileName, activeType, project.name);

                            // For custom paths, ensure the directory exists before creating the file
                            if (useCustomPath) {
                                try {
                                    console.log('********************************* PATH *********************************');
                                    console.log(targetDir);
                                    // Create a .keep file in the directory to ensure it exists
                                    await userActor.createCodeArtifact(
                                        userPrincipal,
                                        project.id,
                                        '.keep',
                                        {Text: ''},
                                        'text/plain',
                                        'plaintext',
                                        targetDir,
                                        versionParam
                                    );

                                    // Brief delay to let the directory creation complete
                                    await new Promise(resolve => setTimeout(resolve, 100));
                                } catch (dirErr) {
                                    console.warn('[IDEStore] Error pre-creating directory, continuing anyway:', dirErr);
                                }
                            }

                            const content = {Text: ''};
                            const mimeType = getMimeType(fileName);
                            const language = getLanguageFromFileName(fileName);

                            console.log('[IDEStore] Creating new file:', {
                                fileName,
                                targetDir,
                                mimeType,
                                language,
                                versionId: currentVersionId
                            });

                            const result = await userActor.createCodeArtifact(
                                userPrincipal,
                                project.id,
                                fileName,
                                content,
                                mimeType,
                                language,
                                targetDir,
                                versionParam
                            );

                            if ('ok' in result) {
                                onSuccess?.("File created successfully");

                                // For UI purposes only, join the path
                                const fullPath = `${targetDir}/${fileName}`;
                                console.log('[IDEStore] File created with path:', fullPath);

                                // Create a proper file object with all backend fields preserved
                                const newFile = {
                                    name: fullPath,          // Display name
                                    content: '',             // Initial content
                                    fullId: result.ok,       // Server ID
                                    path: targetDir,         // ORIGINAL backend path
                                    fileName: fileName,      // ORIGINAL backend filename
                                    id: result.ok            // ORIGINAL backend id
                                };

                                // Create new files array with the new file
                                const updatedFiles = [...files, newFile];

                                // Update the file tree for UI
                                const updatedFileTree = updateFileTreeAfterSave(fileTree, updatedFiles, fullPath);

                                // Update the state preserving editor context
                                set({
                                    files: updatedFiles,
                                    fileTree: updatedFileTree,
                                    activeFile: fullPath,      // Set active file to the new file
                                    fileContent: '',           // Start with empty content
                                    showNewFileDialog: false,
                                    newFileName: '',
                                    newFilePath: null,         // Reset the path
                                    hasUnsavedChanges: false,
                                    loading: false
                                });

                                // Force refresh files from backend to ensure cache is updated
                                // This ensures the new file appears even if cache is checked
                                try {
                                    await get().refreshFiles(currentVersionId || undefined, true);
                                } catch (refreshError) {
                                    console.warn('[IDEStore] Failed to refresh files after creation, but file was created:', refreshError);
                                }

                                // For new files, use the original project update to ensure proper registration
                                if (get().originalProjectUpdate) {
                                    await get().originalProjectUpdate();
                                } else if (get().onProjectUpdate) {
                                    await get().onProjectUpdate();
                                }
                            } else {
                                throw new Error(`Failed to create file: ${result.err}`);
                            }
                        } else {
                            // SAVE EXISTING FILE - use exact backend fields
                            console.log('[IDEStore] Saving existing file:', activeFile);

                            // Find the file in our local state to get the ORIGINAL backend data
                            const fileData = files.find(f => f.name === activeFile);

                            if (!fileData) {
                                throw new Error(`File not found in local state: ${activeFile}`);
                            }

                            console.log('[IDEStore] Found file data:', fileData);

                            // DIRECT APPROACH: Use the file's original backend path and filename
                            if (fileData.path && fileData.fileName) {
                                console.log('[IDEStore] Using exact backend path and filename:', {
                                    path: fileData.path,
                                    fileName: fileData.fileName
                                });

                                const result = await userActor.updateCodeArtifact(
                                    userPrincipal,
                                    project.id,
                                    fileData.fileName,  // EXACT backend fileName
                                    {Text: fileContent},
                                    fileData.path,      // EXACT backend path
                                    versionParam
                                );

                                if ('ok' in result) {
                                    onSuccess?.("File saved successfully");

                                    // Update the file content in our local state without rebuilding tree
                                    // CRITICAL: Use the saved content (which matches backend now)
                                    const updatedFiles = files.map(f =>
                                        f.name === activeFile
                                            ? {...f, content: fileContent}
                                            : f
                                    );

                                    // No need to rebuild tree for content-only changes
                                    // CRITICAL: Clear hasUnsavedChanges so refreshFiles won't preserve old content
                                    set({
                                        files: updatedFiles,
                                        hasUnsavedChanges: false, // Clear unsaved flag so backend content is used
                                        showSaveConfirmation: false,
                                        fileContent: fileContent // Ensure fileContent matches saved content
                                    });
                                    
                                    console.log(`[saveFile] File saved and unsaved flag cleared for ${activeFile} (${fileContent.length} chars). Total files: ${updatedFiles.length}`);

                                    // Skip project update for existing files
                                    return;
                                } else {
                                    throw new Error(result.err);
                                }
                            }

                            // FALLBACK: If we don't have path and fileName directly on the file object,
                            // get the server files to find the exact path components
                            const filesResult = await get().safeGetProjectFiles(
                                project.id,
                                versionParam
                            );

                            if (!('ok' in filesResult)) {
                                throw new Error(`Failed to get project files: ${filesResult.err}`);
                            }

                            // Find a matching file in the server response
                            let serverFileMatch = null;

                            // METHOD 1: Match by fullId if available
                            if (fileData.fullId && typeof fileData.fullId === 'string') {
                                serverFileMatch = filesResult.ok.find(serverFile =>
                                    serverFile.id === fileData.fullId
                                );

                                if (serverFileMatch) {
                                    console.log('[IDEStore] Found match by ID:', serverFileMatch);
                                }
                            }

                            // METHOD 2: Match by display path
                            if (!serverFileMatch) {
                                serverFileMatch = filesResult.ok.find(serverFile =>
                                    (serverFile.fullPath && serverFile.fullPath === fileData.name) ||
                                    (`${serverFile.path}/${serverFile.fileName}` === fileData.name)
                                );

                                if (serverFileMatch) {
                                    console.log('[IDEStore] Found match by path:', serverFileMatch);
                                }
                            }

                            // METHOD 3: Match by filename (if unique)
                            if (!serverFileMatch) {
                                const fileName = fileData.name.substring(fileData.name.lastIndexOf('/') + 1);
                                const matchingFiles = filesResult.ok.filter(serverFile =>
                                    serverFile.fileName === fileName
                                );

                                if (matchingFiles.length === 1) {
                                    serverFileMatch = matchingFiles[0];
                                    console.log('[IDEStore] Found unique match by filename:', serverFileMatch);
                                }
                            }

                            if (!serverFileMatch) {
                                console.error('[IDEStore] Could not find matching file on server');
                                throw new Error(`File not found on server: ${activeFile}`);
                            }

                            console.log('[IDEStore] Using server file match for saving:', {
                                id: serverFileMatch.id,
                                path: serverFileMatch.path,
                                fileName: serverFileMatch.fileName
                            });

                            // Use the exact path and filename from the server
                            const result = await userActor.updateCodeArtifact(
                                userPrincipal,
                                project.id,
                                serverFileMatch.fileName,  // EXACT backend fileName
                                {Text: fileContent},
                                serverFileMatch.path,      // EXACT backend path
                                versionParam
                            );

                            if ('ok' in result) {
                                onSuccess?.("File saved successfully");

                                // Update the file content and store the backend path components
                                // CRITICAL: Use the saved content (which matches backend now)
                                const updatedFiles = files.map(f =>
                                    f.name === activeFile
                                        ? {
                                            ...f,
                                            content: fileContent, // Use saved content
                                            // Store backend fields for future operations
                                            path: serverFileMatch.path,
                                            fileName: serverFileMatch.fileName,
                                            id: serverFileMatch.id
                                        }
                                        : f
                                );

                                // No need to rebuild tree for content-only changes
                                // CRITICAL: Clear hasUnsavedChanges so refreshFiles won't preserve old content
                                set({
                                    files: updatedFiles,
                                    hasUnsavedChanges: false, // Clear unsaved flag so backend content is used
                                    showSaveConfirmation: false,
                                    fileContent: fileContent // Ensure fileContent matches saved content
                                });
                                
                                console.log(`[saveFile] File saved and unsaved flag cleared for ${activeFile} (${fileContent.length} chars). Total files: ${updatedFiles.length}`);
                            } else {
                                throw new Error(result.err);
                            }
                        }
                    } catch (err) {
                        console.error('[IDEStore] Error in saveFile operation:', err);
                        onError?.(err instanceof Error ? err.message : 'Failed to save file');

                        // Only set loading: false for new files
                        if (isNew) {
                            set({loading: false});
                        }
                    } finally {
                        // Only clear loading state for new files
                        if (isNew) {
                            set({loading: false});
                        }
                    }
                },
                showNewFile: () => {
                    console.log('[IDEStore] Opening new file dialog');
                    set({
                        showNewFileDialog: true,
                        showNewFolderDialog: false,
                        newFileName: '',
                        newFilePath: null,
                        // Don't reset useCustomPath to preserve user preference
                    });
                },

                showNewFolder: () => {
                    set({
                        showNewFolderDialog: true,
                        showNewFileDialog: false,
                        newFileName: '',
                        newFilePath: null,
                        // Don't reset useCustomPath to preserve user preference
                    });
                },

                createFolder: async (path) => {
                    const state = get();
                    const {
                        userActor,
                        userPrincipal,
                        project,
                        newFileName,
                        newFilePath,
                        useCustomPath,
                        onSuccess,
                        onError,
                        currentVersionId
                    } = state;

                    if (!userPrincipal || !userActor || !project?.id) {
                        console.error('[IDEStore] Missing required dependencies for folder creation');
                        return;
                    }

                    try {
                        let targetPath = '';

                        // If path is provided directly, use it without normalizing
                        if (path) {
                            targetPath = path;
                        }
                        // Otherwise use the selected directory + new folder name without path manipulation
                        else if (newFilePath && newFileName) {
                            targetPath = `${newFilePath}/${newFileName}`.replace(/\/\/+/g, '/');
                        } else if (newFileName) {
                            // Just the folder name without a path - keep as is
                            targetPath = newFileName;
                        }

                        if (!targetPath) {
                            throw new Error('Folder path is required');
                        }

                        // Use path as-is with no normalization for backend operations
                        const keepFileName = '.keep';

                        // Version parameter in Candid format
                        const versionParam = currentVersionId ? [currentVersionId] : [];

                        console.log('[IDEStore] Creating folder:', {
                            targetPath,
                            keepFileName,
                            versionId: currentVersionId
                        });

                        const content = {Text: ''};

                        const result = await userActor.createCodeArtifact(
                            userPrincipal,
                            project.id,
                            keepFileName,
                            content,
                            'text/plain',
                            'plaintext',
                            targetPath,
                            versionParam
                        );

                        if ('ok' in result) {
                            onSuccess?.('Folder created successfully');

                            // Update UI state
                            set({
                                showNewFolderDialog: false,
                                newFileName: '',
                                newFilePath: null
                                // Don't reset useCustomPath
                            });

                            await get().refreshFiles();

                            const currentProjectId = project.id;
                            console.log(`[IDEStore] Package operation for project: ${currentProjectId}`);

                            const safeUpdateFn = get().safeProjectUpdate;
                            if (safeUpdateFn) {
                                await safeUpdateFn();
                            } else {
                                // Fallback to original but with project ID protection
                                const projectUpdateFn = get().onProjectUpdate;
                                if (projectUpdateFn) {
                                    console.log(`[IDEStore] Running project update for package operation on ${currentProjectId}`);
                                    await projectUpdateFn();

                                    // Verify we're still on the same project after update
                                    const postUpdateProject = get().project;
                                    if (postUpdateProject?.id !== currentProjectId) {
                                        console.warn(`[IDEStore] Project changed during package operation! Expected: ${currentProjectId}, Got: ${postUpdateProject?.id}`);
                                        // Force back to original project if needed
                                        const originalProject = get().project;
                                        if (originalProject?.id !== currentProjectId) {
                                            console.log(`[IDEStore] Forcing project back to ${currentProjectId}`);
                                            // Don't call setProject here - just log the issue
                                        }
                                    }
                                }
                            }

                        } else {
                            throw new Error(result.err);
                        }
                    } catch (err) {
                        console.error('[IDEStore] Error creating folder:', err);
                        onError?.(err instanceof Error ? err.message : 'Failed to create folder');
                    }
                },

                deleteFile: (filePath) => {
                    // Just set up the confirmation dialog
                    set({
                        fileToDelete: filePath,
                        showDeleteConfirmation: true
                    });
                },

                confirmDeleteFile: async () => {
                    const state = get();
                    const {
                        userActor,
                        userPrincipal,
                        project,
                        fileToDelete,
                        files,
                        activeFile,
                        fileTree,
                        onSuccess,
                        onError,
                        currentVersionId
                    } = state;

                    if (!userPrincipal || !userActor || !project?.id || !fileToDelete) {
                        console.error('[IDEStore] Missing required dependencies for file deletion');
                        set({showDeleteConfirmation: false, fileToDelete: null});
                        return;
                    }

                    try {
                        console.log(`[DEBUG] File deletion - Starting for file: ${fileToDelete}`);

                        // Find the file in the files array to get all backend fields
                        const fileToDeleteData = files.find(file => file.name === fileToDelete);

                        if (!fileToDeleteData) {
                            throw new Error('File not found in state');
                        }

                        // Version parameter in Candid format
                        const versionParam = currentVersionId ? [currentVersionId] : [];

                        // APPROACH 1: Use direct backend fields if available on the file object
                        if (fileToDeleteData.path && fileToDeleteData.fileName) {
                            try {
                                console.log(`[DEBUG] Attempting to delete with direct backend fields:`, {
                                    path: fileToDeleteData.path,
                                    fileName: fileToDeleteData.fileName
                                });

                                const result = await userActor.deleteCodeArtifact(
                                    userPrincipal,
                                    project.id,
                                    fileToDeleteData.path,      // EXACT backend path
                                    fileToDeleteData.fileName,  // EXACT backend fileName
                                    versionParam
                                );

                                if ('ok' in result) {
                                    console.log(`[DEBUG] Successfully deleted file using direct backend fields`);

                                    // Update files list excluding the deleted file
                                    const updatedFiles = files.filter(file => file.name !== fileToDelete);

                                    // Update the file tree for UI
                                    const updatedFileTree = updateFileTreeForFile(fileTree, updatedFiles, fileToDelete);

                                    // Handle active file selection - UI only logic
                                    let newActiveFile = activeFile;
                                    let newFileContent = '';

                                    if (activeFile === fileToDelete) {
                                        if (updatedFiles.length > 0) {
                                            // Try to find a suitable file in the same directory
                                            const dirPath = pathUtils.getParentPath(fileToDelete);
                                            const filesInSameDir = updatedFiles.filter(file =>
                                                pathUtils.getParentPath(file.name) === dirPath
                                            );

                                            if (filesInSameDir.length > 0) {
                                                newActiveFile = filesInSameDir[0].name;
                                                newFileContent = typeof filesInSameDir[0].content === 'string'
                                                    ? filesInSameDir[0].content
                                                    : '';
                                            } else {
                                                // Otherwise use the first file
                                                newActiveFile = updatedFiles[0].name;
                                                newFileContent = typeof updatedFiles[0].content === 'string'
                                                    ? updatedFiles[0].content
                                                    : '';
                                            }
                                        } else {
                                            newActiveFile = '';
                                        }
                                    }

                                    // Update state
                                    set({
                                        files: updatedFiles,
                                        fileTree: updatedFileTree,
                                        activeFile: newActiveFile,
                                        fileContent: newFileContent,
                                        showDeleteConfirmation: false,
                                        fileToDelete: null
                                    });

                                    onSuccess?.("File deleted successfully");
                                    return;
                                }
                            } catch (error) {
                                console.log(`[DEBUG] Error deleting with direct backend fields:`, error);
                                // Continue to next approach
                            }
                        }

                        // APPROACH 2: Get all server files and find the matching file
                        const filesResult = await get().safeGetProjectFiles(
                            project.id,
                            versionParam
                        );

                        if ('ok' in filesResult) {
                            // Find a matching file on the server
                            let serverFileMatch = null;

                            // METHOD 1: Match by ID if available
                            if (fileToDeleteData.id || fileToDeleteData.fullId) {
                                const fileId = fileToDeleteData.id || fileToDeleteData.fullId;
                                serverFileMatch = filesResult.ok.find(serverFile =>
                                    serverFile.id === fileId
                                );

                                if (serverFileMatch) {
                                    console.log('[DEBUG] Found file match by ID:', serverFileMatch);
                                }
                            }

                            // METHOD 2: Match by display path
                            if (!serverFileMatch) {
                                serverFileMatch = filesResult.ok.find(serverFile =>
                                    (serverFile.fullPath && serverFile.fullPath === fileToDelete) ||
                                    (`${serverFile.path}/${serverFile.fileName}` === fileToDelete)
                                );

                                if (serverFileMatch) {
                                    console.log('[DEBUG] Found file match by path:', serverFileMatch);
                                }
                            }

                            // METHOD 3: Match by filename (if unique)
                            if (!serverFileMatch) {
                                const fileName = fileToDelete.substring(fileToDelete.lastIndexOf('/') + 1);
                                const matchingFiles = filesResult.ok.filter(serverFile =>
                                    serverFile.fileName === fileName
                                );

                                if (matchingFiles.length === 1) {
                                    serverFileMatch = matchingFiles[0];
                                    console.log('[DEBUG] Found unique file match by filename:', serverFileMatch);
                                }
                            }

                            if (serverFileMatch) {
                                try {
                                    console.log(`[DEBUG] Attempting to delete using server file match:`, {
                                        path: serverFileMatch.path,
                                        fileName: serverFileMatch.fileName
                                    });

                                    const result = await userActor.deleteCodeArtifact(
                                        userPrincipal,
                                        project.id,
                                        serverFileMatch.path,      // EXACT backend path
                                        serverFileMatch.fileName,  // EXACT backend fileName
                                        versionParam
                                    );

                                    if ('ok' in result) {
                                        console.log(`[DEBUG] Successfully deleted file using server match`);

                                        // Update files list excluding the deleted file
                                        const updatedFiles = files.filter(file => file.name !== fileToDelete);

                                        // Update the file tree for UI
                                        const updatedFileTree = updateFileTreeForFile(fileTree, updatedFiles, fileToDelete);

                                        // Handle active file selection
                                        let newActiveFile = activeFile;
                                        let newFileContent = '';

                                        if (activeFile === fileToDelete) {
                                            if (updatedFiles.length > 0) {
                                                // Try to find a suitable file in the same directory
                                                const dirPath = pathUtils.getParentPath(fileToDelete);
                                                const filesInSameDir = updatedFiles.filter(file =>
                                                    pathUtils.getParentPath(file.name) === dirPath
                                                );

                                                if (filesInSameDir.length > 0) {
                                                    newActiveFile = filesInSameDir[0].name;
                                                    newFileContent = typeof filesInSameDir[0].content === 'string'
                                                        ? filesInSameDir[0].content
                                                        : '';
                                                } else {
                                                    // Otherwise use the first file
                                                    newActiveFile = updatedFiles[0].name;
                                                    newFileContent = typeof updatedFiles[0].content === 'string'
                                                        ? updatedFiles[0].content
                                                        : '';
                                                }
                                            } else {
                                                newActiveFile = '';
                                            }
                                        }

                                        // Update state
                                        set({
                                            files: updatedFiles,
                                            fileTree: updatedFileTree,
                                            activeFile: newActiveFile,
                                            fileContent: newFileContent,
                                            showDeleteConfirmation: false,
                                            fileToDelete: null
                                        });

                                        onSuccess?.("File deleted successfully");
                                        return;
                                    }
                                } catch (error) {
                                    console.log(`[DEBUG] Error deleting with server match:`, error);
                                    // Continue to next attempt
                                }
                            }

                            // If we found any matching files by name, try them all
                            const fileName = fileToDelete.substring(fileToDelete.lastIndexOf('/') + 1);
                            const matchingFiles = filesResult.ok.filter(serverFile =>
                                serverFile.fileName === fileName
                            );

                            if (matchingFiles.length > 0) {
                                console.log(`[DEBUG] Trying all ${matchingFiles.length} files with matching filename`);

                                // Try each matching file until one succeeds
                                for (const matchFile of matchingFiles) {
                                    try {
                                        console.log(`[DEBUG] Attempting to delete matching file:`, {
                                            path: matchFile.path,
                                            fileName: matchFile.fileName
                                        });

                                        const result = await userActor.deleteCodeArtifact(
                                            userPrincipal,
                                            project.id,
                                            matchFile.path,      // EXACT backend path
                                            matchFile.fileName,  // EXACT backend fileName
                                            versionParam
                                        );

                                        if ('ok' in result) {
                                            console.log(`[DEBUG] Successfully deleted matching file`);

                                            // Update files list excluding the deleted file
                                            const updatedFiles = files.filter(file => file.name !== fileToDelete);

                                            // Update the file tree for UI
                                            const updatedFileTree = updateFileTreeForFile(fileTree, updatedFiles, fileToDelete);

                                            // Handle active file selection
                                            let newActiveFile = activeFile;
                                            let newFileContent = '';

                                            if (activeFile === fileToDelete) {
                                                if (updatedFiles.length > 0) {
                                                    // Try to find a suitable file in the same directory
                                                    const dirPath = pathUtils.getParentPath(fileToDelete);
                                                    const filesInSameDir = updatedFiles.filter(file =>
                                                        pathUtils.getParentPath(file.name) === dirPath
                                                    );

                                                    if (filesInSameDir.length > 0) {
                                                        newActiveFile = filesInSameDir[0].name;
                                                        newFileContent = typeof filesInSameDir[0].content === 'string'
                                                            ? filesInSameDir[0].content
                                                            : '';
                                                    } else {
                                                        // Otherwise use the first file
                                                        newActiveFile = updatedFiles[0].name;
                                                        newFileContent = typeof updatedFiles[0].content === 'string'
                                                            ? updatedFiles[0].content
                                                            : '';
                                                    }
                                                } else {
                                                    newActiveFile = '';
                                                }
                                            }

                                            // Update state
                                            set({
                                                files: updatedFiles,
                                                fileTree: updatedFileTree,
                                                activeFile: newActiveFile,
                                                fileContent: newFileContent,
                                                showDeleteConfirmation: false,
                                                fileToDelete: null
                                            });

                                            onSuccess?.("File deleted successfully");
                                            return;
                                        }
                                    } catch (matchError) {
                                        console.log(`[DEBUG] Error deleting matching file:`, matchError);
                                        // Continue to the next matching file
                                    }
                                }
                            }
                        }

                        // If we got here, all approaches failed
                        throw new Error(`Failed to delete file: ${fileToDelete}`);
                    } catch (err) {
                        console.error('[DEBUG] Complete error in file deletion:', err);
                        onError?.(err instanceof Error ? err.message : 'Failed to delete file');
                    } finally {
                        // Clean up dialog state even on error
                        set({
                            showDeleteConfirmation: false,
                            fileToDelete: null
                        });
                    }
                },

                safeProjectUpdate: async () => {
                    const state = get();
                    const {userActor, project, onProjectUpdate, onError} = state;

                    if (!userActor || !project) {
                        console.log('[IDEStore] Cannot update project - missing dependencies');
                        return;
                    }

                    try {
                        // Store current project state including canisters
                        const previousCanisters = project.canisters || [];
                        console.log('[IDEStore] Starting safe project update, current canisters:', previousCanisters.length);

                        // Call the original update function
                        if (onProjectUpdate) {
                            await onProjectUpdate();
                        }

                        // Get the updated project state
                        const updatedProject = get().project;

                        // Check if canister information was lost
                        if (previousCanisters.length > 0 &&
                            (!updatedProject?.canisters || updatedProject.canisters.length === 0)) {

                            console.warn('[IDEStore] Canister information was lost during update, restoring...');

                            // Restore canister associations if needed
                            if (userActor && updatedProject) {
                                for (const canisterPrincipal of previousCanisters) {
                                    console.log('[IDEStore] Restoring canister:', canisterPrincipal.toText());
                                    await userActor.addCanisterToProject(
                                        updatedProject.id,
                                        canisterPrincipal
                                    );
                                }

                                // Refresh the project one more time to get updated canister info
                                if (onProjectUpdate) {
                                    await onProjectUpdate();
                                }
                            }
                        }

                        console.log('[IDEStore] Safe project update complete');
                    } catch (error) {
                        console.error('[IDEStore] Error in safe project update:', error);
                        onError?.(error instanceof Error ? error.message : 'Project update failed');
                    }
                },

                handleClaudeContentUpdate: (newContent) => {
                    const state = get();
                    const {editorRef, activeFile, files, fileTree} = state;

                    if (!editorRef?.current) {
                        console.error('[IDEStore] Cannot update content - editor reference not available');
                        return;
                    }

                    console.log('[IDEStore] Updating content from Claude assistant');

                    try {
                        // Update the editor content
                        editorRef.current.setValue(newContent);

                        // Update content in files array without rebuilding tree
                        const updatedFiles = files.map(file =>
                            file.name === activeFile
                                ? {...file, content: newContent}
                                : file
                        );

                        // Update content in state without rebuilding tree
                        set({
                            files: updatedFiles,
                            fileContent: newContent,
                            hasUnsavedChanges: true
                        });

                        // Trigger diagnostics if language service is available
                        const {languageService} = state;
                        if (languageService && activeFile) {
                            console.log('[IDEStore] Running diagnostics on updated content');
                            setTimeout(() => {
                                try {
                                    languageService.handleDiagnostics(activeFile, newContent);
                                } catch (error) {
                                    console.error('[IDEStore] Error running diagnostics on Claude content:', error);
                                }
                            }, 500);
                        }
                    } catch (error) {
                        console.error('[IDEStore] Error updating content from Claude:', error);
                    }
                },

                getProjectVersions: async () => {
                    const state = get();

                    if (!state.project || state.project.id === 'none' || !state.userActor) {
                        return;
                    }

                    try {
                        set({
                            isLoadingVersions: true,
                            // Always clear versions array when starting a fresh fetch
                            projectVersions: []
                        });

                        console.log(`[IDEStore] Fetching versions for project: ${state.project.id}`);

                        // Call the backend to fetch project versions
                        const result = await state.userActor.getProjectVersions(state.project.id);

                        if ('ok' in result) {
                            // Sort versions by semantic version (newest first)
                            const sortedVersions = result.ok.sort((a, b) => {
                                const versionA = a.semanticVersion;
                                const versionB = b.semanticVersion;

                                // Convert BigInt to Number safely
                                const majorA = typeof versionA.major === 'bigint' ? Number(versionA.major) : versionA.major;
                                const majorB = typeof versionB.major === 'bigint' ? Number(versionB.major) : versionB.major;

                                if (majorA !== majorB) return majorB - majorA;

                                const minorA = typeof versionA.minor === 'bigint' ? Number(versionA.minor) : versionA.minor;
                                const minorB = typeof versionB.minor === 'bigint' ? Number(versionB.minor) : versionB.minor;

                                if (minorA !== minorB) return minorB - minorA;

                                const patchA = typeof versionA.patch === 'bigint' ? Number(versionA.patch) : versionA.patch;
                                const patchB = typeof versionB.patch === 'bigint' ? Number(versionB.patch) : versionB.patch;

                                return patchB - patchA;
                            });

                            console.log(`[IDEStore] Found ${sortedVersions.length} versions for project ${state.project.id}`);

                            // Just set the versions array without changing currentVersionId
                            set({
                                projectVersions: sortedVersions
                                // Remove this line to prevent auto-selecting the latest version
                                // currentVersionId: sortedVersions.length > 0 && !state.currentVersionId ? sortedVersions[0].id : state.currentVersionId
                            });

                            // Don't automatically refresh with a version
                            // Remove or comment out this block
                            /*
                            if (sortedVersions.length > 0 && !state.currentVersionId) {
                                await state.refreshFiles(sortedVersions[0].id);
                            }
                            */

                        } else {
                            console.log("[ERROR] Failed to get project versions:", result.err);
                            if (state.onError) state.onError(`Failed to get project versions: ${result.err}`);

                            // Clear versions array on error
                            set({projectVersions: []});
                        }
                    } catch (error) {
                        if (state.onError) {
                            const errorMessage = error instanceof Error
                                ? error.message
                                : String(error);
                            state.onError(`Error fetching version status: ${errorMessage}`);
                        }
                        console.error("[ERROR] Error fetching versions:", error);

                        // Clear versions array on error
                        set({projectVersions: []});
                    } finally {
                        set({isLoadingVersions: false});
                    }
                },

                setProject: async (project) => {
                    const state = get();
                    const currentProjectId = state.project?.id;
                    
                    // Clear cached package.json path when switching projects
                    if (currentProjectId !== project.id) {
                        set({ cachedPackageJsonPath: null });
                    }

                    // 🚫 CRITICAL: Check if this is a system-triggered switch during protection period
                    const protection = state.projectSwitchProtection;
                    const now = Date.now();
                    const timeSinceUserSwitch = now - protection.lastUserSwitchTime;

                    if (protection.isLocked &&
                        protection.lastUserSwitchProjectId !== project.id &&
                        timeSinceUserSwitch < 5000) { // 5 second protection
                        console.log(`🚫 [IDE] BLOCKED automatic project switch to ${project.id}`);
                        return; // EXIT EARLY - DO NOT SWITCH
                    }

                    // Immediately update project reference
                    set({project});

                    if (!currentProjectId || currentProjectId !== project.id) {
                        console.log('[IDEStore] Project changed, doing coordinated reset');

                        // Cancel any pending operations
                        if (state.diagnosticsTimeout) {
                            clearTimeout(state.diagnosticsTimeout);
                        }

                        // IMPORTANT: Dispose existing language service first
                        if (state.languageService) {
                            console.log('[IDEStore] Disposing existing language service for project change');
                            state.languageService.dispose();
                        }

                        // Reset ALL project-specific state immediately
                        set({
                            activeFile: '',
                            fileContent: '',
                            hasUnsavedChanges: false,
                            files: [],
                            fileTree: null,
                            currentPath: '/',
                            currentVersionId: null,
                            projectVersions: [],
                            isLoadingVersions: false,
                            hasRunInitialPackageUpdate: false,
                            hasRunInitialMopsTomlUpdate: false,
                            languageService: null, // Clear language service
                            cachedPackageJsonPath: null, // Clear cached path on project change
                        });
                        

                        // Clear editor content immediately
                        const editorRef = state.editorRef;
                        if (editorRef?.current) {
                            editorRef.current.setValue('');
                        }

                        if (project.id !== 'none') {
                            try {
                                // AWAIT the loading to complete
                                await state.loadProjectData(project.id);
                            } catch (error) {
                                console.error('[IDEStore] Error in coordinated project loading:', error);
                                // Reset loading state on error
                                if (get().project?.id === project.id) {
                                    set({loading: false, isLoadingVersions: false});
                                }
                            }
                        }
                    } else {
                        console.log('[IDEStore] Project updated with same ID, maintaining version state');
                    }
                },

                loadProjectData: async (projectId) => {
                    const state = get();
                    console.log(`[loadProjectData] Starting coordinated load for project: ${projectId}`);

                    try {
                        // Step 1: Load files ONLY - this is what users see immediately
                        console.log(`[loadProjectData] Step 1: Loading files for project: ${projectId}`);
                        await state.refreshFiles(null);

                        // Check if project changed during file loading
                        if (get().project?.id !== projectId) {
                            console.log('[loadProjectData] Project changed during file loading, aborting');
                            return;
                        }

                        // Step 2: Set default file immediately so user can start working
                        console.log(`[loadProjectData] Step 2: Setting default file for project: ${projectId}`);
                        setTimeout(() => {
                            if (get().project?.id === projectId) {
                                get().setDefaultFileForActiveType();
                            }
                        }, 50); // Minimal delay

                        // Step 3: Run everything else in parallel in the background
                        console.log(`[loadProjectData] Step 3: Starting background operations for project: ${projectId}`);

                        // Don't await these - let them run in background
                        Promise.all([
                            state.getProjectVersions().catch(err =>
                                console.warn('[loadProjectData] Version loading failed:', err)
                            ),
                            state.parsePackageJson().catch(err =>
                                console.warn('[loadProjectData] Package.json parsing failed:', err)
                            ),
                            state.parseMopsToml().catch(err =>
                                console.warn('[loadProjectData] Mops.toml parsing failed:', err)
                            )
                        ]).then(() => {
                            // After parsing, update package files in parallel
                            if (get().project?.id === projectId) {
                                Promise.all([
                                    state.updatePackageFile().catch(err =>
                                        console.warn('[loadProjectData] Package file update failed:', err)
                                    ),
                                    state.updateMopsTomlFile().catch(err =>
                                        console.warn('[loadProjectData] Mops.toml update failed:', err)
                                    )
                                ]).catch(err => {
                                    console.warn('[loadProjectData] Package updates failed:', err);
                                });
                            }
                        });

                        console.log(`[loadProjectData] Initial load completed for project: ${projectId}`);

                    } catch (error) {
                        console.error('[loadProjectData] Error:', error);
                        state.onError?.(`Failed to load project data: ${(error as Error).message}`);
                    } finally {
                        // Clear loading state immediately after files are loaded
                        if (get().project?.id === projectId) {
                            console.log(`[loadProjectData] Clearing loading states for project: ${projectId}`);
                            set({loading: false, isLoadingVersions: false});
                        }
                    }
                },

                getLatestProjectVersion: async () => {
                    const {userActor, project, onError} = get();

                    if (!userActor || !project?.id) {
                        console.error('[IDEStore] Missing dependencies for fetching latest version');
                        return;
                    }

                    try {
                        set({loading: true});

                        // First try to get all versions
                        const versionsResult = await userActor.getProjectVersions(project.id);

                        if ('ok' in versionsResult && versionsResult.ok.length > 0) {
                            // Get the first version (assumed to be latest)
                            const latestVersion = versionsResult.ok[0];
                            set({currentVersionId: latestVersion.id});
                            console.log('[IDEStore] Using latest version:', latestVersion.id);

                            // Refresh files for this version
                            await get().refreshFiles().then(() => {
                                // Call our default file function after files are loaded
                                get().setDefaultFileForActiveType();
                            });
                        } else {
                            // If no versions exist, try to initialize one
                            console.log('[IDEStore] No versions found, initializing...');
                            await get().initializeProjectVersion();
                        }
                    } catch (error) {
                        onError?.(`Failed to fetch latest version: ${error}`);
                    } finally {
                        set({loading: false});
                    }
                },

                initializeProjectVersion: async () => {
                    const {userActor, project, onError, onSuccess} = get();

                    if (!userActor || !project?.id) {
                        console.error('[IDEStore] Missing dependencies for initializing version');
                        return;
                    }

                    try {
                        set({loading: true, isLoadingVersions: true});

                        console.log('[IDEStore] Initializing first version for project:', project.id);
                        const result = await userActor.initializeProjectVersion(project.id);

                        if ('ok' in result) {
                            set({currentVersionId: result.ok.id});
                            onSuccess?.(`Initialized version ${result.ok.semanticVersion.major}.${result.ok.semanticVersion.minor}.${result.ok.semanticVersion.patch}`);
                            console.log('[IDEStore] Initialized version:', result.ok.id);

                            // Important: After initializing a version, refresh the versions list
                            await get().getProjectVersions();

                            // Then refresh files for the new version
                            await get().refreshFiles(result.ok.id).then(() => {
                                // Call our default file function after files are loaded
                                get().setDefaultFileForActiveType();
                            });
                        } else {
                            onError?.(result.err);
                        }
                    } catch (error) {
                        onError?.(`Failed to initialize version: ${error}`);
                    } finally {
                        set({loading: false, isLoadingVersions: false});
                    }
                },

                setDefaultFileForActiveType: () => {
                    const state = get();
                    const startingProjectId = state.project?.id;
                    const {files, activeType, project, setActiveFile, activeFile} = state;

                    if (!project || !files.length || !startingProjectId) return;

                    // CRITICAL CHANGE: If there's already an active file, don't change it
                    // This prevents unwanted jumps to App.tsx or main.mo when editing other files
                    if (activeFile) {
                        console.log(`[DEBUG] Current file already set to: ${activeFile}, keeping it active`);
                        return;
                    }

                    // Only proceed to select a default file if no file is currently active
                    console.log(`[DEBUG] No active file set, selecting default for ${activeType} mode`);

                    // Filter out .keep files
                    const validFiles = files.filter(file => !file.name.includes('.keep'));
                    if (validFiles.length === 0) return;

                    if (activeType === 'backend') {
                        // SIMPLIFIED: Just find the first main.mo file (case-insensitive)
                        const mainMoFile = validFiles.find(file =>
                            file.name.toLowerCase().endsWith('main.mo') ||
                            file.name.toLowerCase().endsWith('/main.mo')
                        );

                        if (mainMoFile) {
                            if (get().project?.id === startingProjectId) {
                                console.log(`[DEBUG] Found main.mo, setting as active file: ${mainMoFile.name}`);
                                setActiveFile(mainMoFile.name);
                            }
                            return;
                        }

                        // Second priority: any .mo file
                        const moFile = validFiles.find(file => file.name.endsWith('.mo'));

                        if (moFile) {
                            if (get().project?.id === startingProjectId) {
                                console.log(`[DEBUG] No main.mo found, using first .mo file: ${moFile.name}`);
                                setActiveFile(moFile.name);
                            }
                            return;
                        }
                    } else { // Frontend
                        // SIMPLIFIED: Just find the first App.tsx file (case-insensitive)
                        const appTsxFile = validFiles.find(file =>
                            file.name.toLowerCase().endsWith('app.tsx') ||
                            file.name.toLowerCase().endsWith('/app.tsx')
                        );

                        if (appTsxFile) {
                            if (get().project?.id === startingProjectId) {
                                console.log(`[DEBUG] Found App.tsx, setting as active file: ${appTsxFile.name}`);
                                setActiveFile(appTsxFile.name);
                            }
                            return;
                        }

                        // If no App.tsx is found, try to find index.tsx or index.js
                        const indexFile = validFiles.find(file =>
                            file.name.toLowerCase().endsWith('index.tsx') ||
                            file.name.toLowerCase().endsWith('index.js')
                        );

                        if (indexFile) {
                            if (get().project?.id === startingProjectId) {
                                console.log(`[DEBUG] Using index file: ${indexFile.name}`);
                                setActiveFile(indexFile.name);
                            }
                            return;
                        }

                        // Second priority: any frontend file (.tsx, .ts, .jsx, .js)
                        const frontendFile = validFiles.find(file =>
                            file.name.endsWith('.tsx') || file.name.endsWith('.ts') ||
                            file.name.endsWith('.jsx') || file.name.endsWith('.js')
                        );

                        if (frontendFile) {
                            if (get().project?.id === startingProjectId) {
                                console.log(`[DEBUG] No App.tsx found, using first frontend file: ${frontendFile.name}`);
                                setActiveFile(frontendFile.name);
                            }
                            return;
                        }
                    }

                    // If we haven't found any suitable file for the active type,
                    // let's try to find any file matching the active type
                    const anyMatchingFiles = validFiles.filter(file => {
                        if (activeType === 'backend') {
                            return file.name.endsWith('.mo');
                        } else {
                            return file.name.endsWith('.tsx') || file.name.endsWith('.ts') ||
                                file.name.endsWith('.jsx') || file.name.endsWith('.js') ||
                                file.name.endsWith('.html') || file.name.endsWith('.css');
                        }
                    });

                    if (anyMatchingFiles.length > 0) {
                        if (get().project?.id === startingProjectId) {
                            console.log(`[DEBUG] Using any ${activeType} file: ${anyMatchingFiles[0].name}`);
                            setActiveFile(anyMatchingFiles[0].name);
                        }
                        return;
                    }

                    // Last resort: use any valid file
                    if (validFiles.length > 0) {
                        if (get().project?.id === startingProjectId) {
                            console.log(`[DEBUG] No suitable ${activeType} files found, using first available file: ${validFiles[0].name}`);
                            setActiveFile(validFiles[0].name);
                        }
                    }
                },

                setCompilationStatus: (status) => {
                    set({compilationStatus: status});
                },

                addMotokoPackage: async (packageInfo) => {
                    const state = get();
                    const {userActor, project, onSuccess, onError, currentVersionId} = state;

                    const versionParam = currentVersionId ? [currentVersionId] : [];

                    if (!userActor || !project?.id) {
                        console.error('[IDEStore] Missing dependencies for adding Motoko package');
                        return null;
                    }

                    try {
                        console.log('[IDEStore] Adding package:', packageInfo);
                        set({isAddingPackage: true});

                        // Normalize package info to match expected format
                        const normalizedPackageInfo: PackageInfo = {
                            ...packageInfo,
                            dir: packageInfo.dir?.length ? [packageInfo.dir[0]] : [],
                            homepage: packageInfo.homepage?.length ? [packageInfo.homepage[0]] : []
                        };

                        const result = await userActor.addPackageToProject(project.id, normalizedPackageInfo, versionParam);

                        if ('ok' in result) {
                            onSuccess?.(result.ok);

                            set({
                                hasRunInitialMopsTomlUpdate: false,
                                motokoPackagesLastUpdated: Date.now()
                            });

                            // Update last modified time
                            set({motokoPackagesLastUpdated: Date.now()});

                            // 🔒 SAFE project update - don't trigger full project switch
                            const currentProjectId = project.id;
                            console.log(`[IDEStore] Adding Motoko package to project: ${currentProjectId}`);

                            // Use the SAFE project update instead of the raw onProjectUpdate
                            const safeUpdateFn = get().safeProjectUpdate;
                            if (safeUpdateFn) {
                                await safeUpdateFn();
                            } else {
                                // Fallback to original but with project ID protection
                                const projectUpdateFn = get().onProjectUpdate;
                                if (projectUpdateFn) {
                                    console.log(`[IDEStore] Running project update for Motoko package add on ${currentProjectId}`);
                                    await projectUpdateFn();

                                    // Verify we're still on the same project after update
                                    const postUpdateProject = get().project;
                                    if (postUpdateProject?.id !== currentProjectId) {
                                        console.warn(`[IDEStore] Project changed during Motoko package add! Expected: ${currentProjectId}, Got: ${postUpdateProject?.id}`);
                                        // Force back to original project if needed
                                        const originalProject = get().project;
                                        if (originalProject?.id !== currentProjectId) {
                                            console.log(`[IDEStore] Forcing project back to ${currentProjectId}`);
                                            // Don't call setProject here - just log the issue
                                        }
                                    }
                                }
                            }

                            // Additional refresh logic after safe project update
                            try {
                                // Refresh files and update language service
                                const {userPrincipal, monaco, editorRef, compilerService} = get();

                                if (userPrincipal && monaco && editorRef?.current && compilerService) {
                                    const [freshProjectData, filesResult] = await Promise.all([
                                        userActor.getUserProjects().then(projects =>
                                            projects.find(p => p.id === currentProjectId) // Use currentProjectId instead of project.id
                                        ),
                                        get().safeGetProjectFiles(currentProjectId, versionParam) // Use currentProjectId
                                    ]);

                                    if (freshProjectData && 'ok' in filesResult) {
                                        const freshFiles = filesResult.ok.map((file: any) => ({
                                            name: pathUtils.joinPaths(file.path, file.fileName),
                                            content: file.content?.[0]?.Text || '',
                                            fullId: file.id
                                        }));

                                        // Update project in state
                                        set({project: freshProjectData});

                                        // Recreate language service with updated project and files
                                        const {languageService} = get();
                                        if (languageService) {
                                            languageService.dispose();
                                        }

                                        const newLanguageService = new LanguageService(
                                            monaco,
                                            editorRef.current,
                                            compilerService,
                                            freshProjectData,
                                            freshFiles
                                        );

                                        set({
                                            languageService: newLanguageService,
                                            files: freshFiles,
                                            fileTree: buildFileTree(freshFiles)
                                        });

                                        // Run diagnostics on current file
                                        const content = editorRef.current.getValue() || '';
                                        const {activeFile} = get();
                                        if (activeFile) {
                                            await newLanguageService.handleDiagnostics(activeFile, content);
                                        }
                                    }
                                }
                            } catch (err) {
                                console.error('[IDEStore] Error updating after Motoko package add:', err);
                            }

                            return result.ok;
                        } else {
                            onError?.(result.err);
                            return result.err;
                        }
                    } catch (err) {
                        console.error("[IDEStore] Error adding Motoko package:", err);
                        onError?.(err instanceof Error ? err.message : 'Failed to add package');
                        return err instanceof Error ? err.message : String(err);
                    } finally {
                        set({isAddingPackage: false});
                    }

                    return null;
                },


                removeMotokoPackage: async (packageName) => {
                    const state = get();
                    const {userActor, project, onSuccess, onError, currentVersionId} = state;

                    const versionParam = currentVersionId ? [currentVersionId] : [];

                    if (!userActor || !project?.id) {
                        console.error('[IDEStore] Missing dependencies for removing Motoko package');
                        return null;
                    }

                    try {
                        const result = await userActor.removePackageFromProject(project.id, packageName);

                        if ('ok' in result) {
                            onSuccess?.("Package removed successfully");

                            set({
                                hasRunInitialMopsTomlUpdate: false,
                                motokoPackagesLastUpdated: Date.now()
                            });

                            // Update last modified time
                            set({motokoPackagesLastUpdated: Date.now()});

                            // 🔒 SAFE project update - don't trigger full project switch
                            const currentProjectId = project.id;
                            console.log(`[IDEStore] Removing Motoko package from project: ${currentProjectId}`);

                            // Use the SAFE project update instead of the raw onProjectUpdate
                            const safeUpdateFn = get().safeProjectUpdate;
                            if (safeUpdateFn) {
                                await safeUpdateFn();
                            } else {
                                // Fallback to original but with project ID protection
                                const projectUpdateFn = get().onProjectUpdate;
                                if (projectUpdateFn) {
                                    console.log(`[IDEStore] Running project update for Motoko package removal on ${currentProjectId}`);
                                    await projectUpdateFn();

                                    // Verify we're still on the same project after update
                                    const postUpdateProject = get().project;
                                    if (postUpdateProject?.id !== currentProjectId) {
                                        console.warn(`[IDEStore] Project changed during Motoko package removal! Expected: ${currentProjectId}, Got: ${postUpdateProject?.id}`);
                                        // Force back to original project if needed
                                        const originalProject = get().project;
                                        if (originalProject?.id !== currentProjectId) {
                                            console.log(`[IDEStore] Forcing project back to ${currentProjectId}`);
                                            // Don't call setProject here - just log the issue
                                        }
                                    }
                                }
                            }

                            // Additional refresh logic after safe project update
                            try {
                                // Refresh files and update language service
                                const {userPrincipal, monaco, editorRef, compilerService} = get();

                                if (userPrincipal && monaco && editorRef?.current && compilerService) {
                                    const [freshProjectData, filesResult] = await Promise.all([
                                        userActor.getUserProjects().then(projects =>
                                            projects.find(p => p.id === currentProjectId) // Use currentProjectId instead of project.id
                                        ),
                                        get().safeGetProjectFiles(currentProjectId, versionParam) // Use currentProjectId
                                    ]);

                                    if (freshProjectData && 'ok' in filesResult) {
                                        const freshFiles = filesResult.ok.map((file: any) => ({
                                            name: pathUtils.joinPaths(file.path, file.fileName),
                                            content: file.content?.[0]?.Text || '',
                                            fullId: file.id
                                        }));

                                        // Update project in state
                                        set({project: freshProjectData});

                                        // Recreate language service with updated project and files
                                        const {languageService} = get();
                                        if (languageService) {
                                            languageService.dispose();
                                        }

                                        const newLanguageService = new LanguageService(
                                            monaco,
                                            editorRef.current,
                                            compilerService,
                                            freshProjectData,
                                            freshFiles
                                        );

                                        set({
                                            languageService: newLanguageService,
                                            files: freshFiles,
                                            fileTree: buildFileTree(freshFiles)
                                        });

                                        // Run diagnostics on current file
                                        const content = editorRef.current.getValue() || '';
                                        const {activeFile} = get();
                                        if (activeFile) {
                                            await newLanguageService.handleDiagnostics(activeFile, content);
                                        }
                                    }
                                }
                            } catch (err) {
                                console.error('[IDEStore] Error updating after Motoko package removal:', err);
                            }

                            return "Package removed successfully";
                        } else {
                            onError?.(result.err);
                            return result.err;
                        }
                    } catch (err) {
                        console.error("[IDEStore] Error removing Motoko package:", err);
                        onError?.(err instanceof Error ? err.message : 'Failed to remove package');
                        return err instanceof Error ? err.message : String(err);
                    }

                    return null;
                },


                addNPMPackage: async (packageInfo) => {
                    const state = get();
                    const {userActor, project, onSuccess, onError, currentVersionId} = state;

                    if (!userActor || !project?.id) {
                        console.error('[IDEStore] Missing dependencies for adding NPM package');
                        return null;
                    }

                    try {
                        console.log("[IDEStore] Adding NPM package:", packageInfo);
                        set({isAddingPackage: true});

                        const result = await userActor.addNpmPackageToProject(project.id, packageInfo);

                        if ('ok' in result) {
                            onSuccess?.(result.ok);

                            // Set the force flag AFTER the backend operation succeeds
                            set({
                                npmPackagesLastUpdated: Date.now(),
                                needsTypeDefinitionRefresh: true,
                                isPackageUpdateForced: true  // This will trigger the useEffect
                            });


                            const currentProjectId = project.id;
                            console.log(`[IDEStore] Package operation for project: ${currentProjectId}`);

                            const safeUpdateFn = get().safeProjectUpdate;
                            if (safeUpdateFn) {
                                await safeUpdateFn();
                            } else {
                                // Fallback to original but with project ID protection
                                const projectUpdateFn = get().onProjectUpdate;
                                if (projectUpdateFn) {
                                    console.log(`[IDEStore] Running project update for package operation on ${currentProjectId}`);
                                    await projectUpdateFn();

                                    // Verify we're still on the same project after update
                                    const postUpdateProject = get().project;
                                    if (postUpdateProject?.id !== currentProjectId) {
                                        console.warn(`[IDEStore] Project changed during package operation! Expected: ${currentProjectId}, Got: ${postUpdateProject?.id}`);
                                        // Force back to original project if needed
                                        const originalProject = get().project;
                                        if (originalProject?.id !== currentProjectId) {
                                            console.log(`[IDEStore] Forcing project back to ${currentProjectId}`);
                                            // Don't call setProject here - just log the issue
                                        }
                                    }
                                }
                            }

                            return result.ok;
                        } else {
                            console.log("[IDEStore] Error adding NPM package:", result.err);
                            onError?.(result.err);
                            return result.err;
                        }
                    } catch (err) {
                        console.error("[IDEStore] Error in addNPMPackage:", err);
                        onError?.(err instanceof Error ? err.message : 'Failed to add NPM package');
                        return err instanceof Error ? err.message : String(err);
                    } finally {
                        // Don't reset isPackageUpdateForced here - let updatePackageFile reset it
                        set({isAddingPackage: false});
                    }
                },

                removeNPMPackage: async (packageName) => {
                    const state = get();
                    const {userActor, project, onSuccess, onError, currentVersionId} = state;

                    if (!userActor || !project?.id) {
                        console.error('[IDEStore] Missing dependencies for removing NPM package');
                        return null;
                    }

                    try {
                        const result = await userActor.removeNpmPackageFromProject(project.id, packageName);

                        if ('ok' in result) {
                            onSuccess?.("NPM package removed successfully");

                            // Set the force flag AFTER the backend operation succeeds
                            set({
                                npmPackagesLastUpdated: Date.now(),
                                needsTypeDefinitionRefresh: true,
                                isPackageUpdateForced: true  // This will trigger the useEffect
                            });

                            const currentProjectId = project.id;
                            console.log(`[IDEStore] Package operation for project: ${currentProjectId}`);

                            const safeUpdateFn = get().safeProjectUpdate;
                            if (safeUpdateFn) {
                                await safeUpdateFn();
                            } else {
                                // Fallback to original but with project ID protection
                                const projectUpdateFn = get().onProjectUpdate;
                                if (projectUpdateFn) {
                                    console.log(`[IDEStore] Running project update for package operation on ${currentProjectId}`);
                                    await projectUpdateFn();

                                    // Verify we're still on the same project after update
                                    const postUpdateProject = get().project;
                                    if (postUpdateProject?.id !== currentProjectId) {
                                        console.warn(`[IDEStore] Project changed during package operation! Expected: ${currentProjectId}, Got: ${postUpdateProject?.id}`);
                                        // Force back to original project if needed
                                        const originalProject = get().project;
                                        if (originalProject?.id !== currentProjectId) {
                                            console.log(`[IDEStore] Forcing project back to ${currentProjectId}`);
                                            // Don't call setProject here - just log the issue
                                        }
                                    }
                                }
                            }

                            return "NPM package removed successfully";
                        } else {
                            onError?.(result.err);
                            return result.err;
                        }
                    } catch (err) {
                        onError?.(err instanceof Error ? err.message : 'Failed to remove NPM package');
                        return err instanceof Error ? err.message : String(err);
                    }
                    // Note: No finally block that resets isPackageUpdateForced
                },

                updatePackageFile: async () => {
                    const state = get();
                    const {monaco, project, userPrincipal, userActor, onError, currentVersionId, files} = state;

                    if (!monaco || !project.npmPackages || project.id === 'none' || !project.name) {
                        console.log("[IDEStore] Missing dependencies for package.json update:", {
                            hasMonaco: !!monaco,
                            hasNpmPackages: !!project.npmPackages,
                            projectId: project.id,
                            projectName: project.name
                        });
                        return;
                    }

                    // CRITICAL: Don't overwrite package.json if it was manually saved recently (within last 10 seconds)
                    const timeSinceLastSave = Date.now() - (state.packageJsonLastSaved || 0);
                    if (timeSinceLastSave < 10000) {
                        console.log(`[IDEStore] Skipping package.json update - file was manually saved ${Math.round(timeSinceLastSave / 1000)}s ago`);
                        return;
                    }

                    const isInitialLoad = !state.hasRunInitialPackageUpdate;
                    const isForceUpdate = state.isPackageUpdateForced === true;
                    const hasPackages = project.npmPackages?.[0]?.length > 0;

                    console.log("[IDEStore] Package.json update - isInitialLoad:", isInitialLoad, "isForceUpdate:", isForceUpdate, "hasPackages:", hasPackages);

                    try {
                        console.log(`[IDEStore] Starting package.json update with version:`, currentVersionId);

                        // OPTIMIZATION: Use existing files from state instead of fetching
                        // Only fetch if we don't have files or need to check for package.json location
                        let currentFiles = [];
                        const versionParam = currentVersionId ? [currentVersionId] : [];
                        
                        // Use cached files from state if available (much faster)
                        if (files && files.length > 0) {
                            currentFiles = files.map((file: any) => ({
                                name: file.name,
                                content: typeof file.content === 'string' ? file.content : '',
                                fullId: file.id,
                                path: file.path || '',
                                fileName: file.fileName || file.name.split('/').pop() || ''
                            }));
                            console.log(`[IDEStore] Using ${currentFiles.length} cached files for package.json analysis (no fetch needed)`);
                        } else if (userPrincipal && userActor && project.id) {
                            // Only fetch if we don't have cached files
                            try {
                                const filesResult = await get().safeGetProjectFiles(
                                    project.id,
                                    versionParam
                                );

                                if ('ok' in filesResult) {
                                    currentFiles = filesResult.ok.map((file: any) => ({
                                        name: pathUtils.joinPaths(file.path, file.fileName),
                                        content: file.content?.[0]?.Text || '',
                                        fullId: file.id,
                                        path: file.path,
                                        fileName: file.fileName
                                    }));
                                    console.log(`[IDEStore] Fetched ${currentFiles.length} files for package.json analysis`);
                                }
                            } catch (fetchError) {
                                console.error("[IDEStore] Error fetching files for package.json analysis:", fetchError);
                                return;
                            }
                        }

                        // Check if package.json already exists
                        const existingPackageJsonFile = currentFiles.find(file =>
                            file.name.endsWith('package.json')
                        );
                        
                        // Cache the package.json path if found
                        if (existingPackageJsonFile) {
                            const pathParts = existingPackageJsonFile.name.split('/');
                            const packageJsonPath = pathParts.slice(0, -1).join('/') || (project.name + '/src/frontend');
                            set({ cachedPackageJsonPath: packageJsonPath });
                            console.log(`[IDEStore] Cached package.json path: ${packageJsonPath}`);
                        }

                        let shouldUpdatePackageJson = false;
                        let packageJsonContent = '';
                        let generatorMode: 'create' | 'update' | 'preserve' = 'update';

                        if (isForceUpdate) {
                            // For forced updates, use update mode which provides smart defaults
                            console.log("[IDEStore] FORCED UPDATE - using update mode with smart defaults");
                            generatorMode = 'update';
                            shouldUpdatePackageJson = true;

                        } else if (isInitialLoad) {
                            // For initial load, only create if it doesn't exist and project needs it
                            if (existingPackageJsonFile) {
                                console.log("[IDEStore] Package.json already exists, skipping initial generation");
                                set({hasRunInitialPackageUpdate: true});
                                return;
                            }

                            // Check if this is a React/frontend project that needs package.json
                            const hasReactFiles = currentFiles.some(file =>
                                file.name.endsWith('.tsx') ||
                                file.name.endsWith('.jsx') ||
                                file.content.includes('import React') ||
                                file.content.includes('from "react"') ||
                                file.content.includes("from 'react'")
                            );

                            const hasICPStudioStructure = currentFiles.some(file =>
                                file.name.includes('/src/frontend/') ||
                                file.name.includes('/src/backend/')
                            );

                            const isReactProject = hasReactFiles || hasICPStudioStructure;

                            if (isReactProject) {
                                console.log("[IDEStore] React project detected, creating initial package.json with defaults");
                                generatorMode = 'create';
                                shouldUpdatePackageJson = true;
                            } else {
                                console.log("[IDEStore] Not a React project, skipping package.json creation");
                                set({hasRunInitialPackageUpdate: true});
                                return;
                            }
                        } else if (hasPackages) {
                            // Update package.json whenever there are packages to write
                            console.log("[IDEStore] Packages detected, updating package.json");

                            if (existingPackageJsonFile) {
                                console.log("[IDEStore] Updating existing package.json with current packages");
                                generatorMode = 'update'; // Smart update with fallbacks
                            } else {
                                console.log("[IDEStore] Creating new package.json for packages");
                                generatorMode = 'create'; // New file with essentials
                            }

                            shouldUpdatePackageJson = true;
                        } else {
                            console.log("[IDEStore] No packages and no special conditions - skipping package.json update");
                            return;
                        }

                        if (!shouldUpdatePackageJson) {
                            console.log("[IDEStore] No package.json update needed");
                            return;
                        }

                        console.log(`[IDEStore] Proceeding with package.json generation in '${generatorMode}' mode`);

                        // Use the new PackageJSONGenerator with mode
                        const generator = new PackageJSONGenerator(project, generatorMode);
                        packageJsonContent = generator.generatePackageJSON();

                        console.log("[IDEStore] Package.json content preview:", packageJsonContent.substring(0, 200) + "...");

                        // Save the package.json file - pass the path to avoid file fetch
                        const packageJsonPath = existingPackageJsonFile 
                            ? (existingPackageJsonFile.path || existingPackageJsonFile.name.split('/').slice(0, -1).join('/'))
                            : undefined;
                        await get().savePackageJSON(packageJsonContent, isInitialLoad, packageJsonPath);

                        // Update TypeScript definitions
                        const npmPackages = project.npmPackages[0] || [];
                        if (npmPackages.length > 0) {
                            console.log("[IDEStore] Updating TypeScript definitions for", npmPackages.length, "packages");
                            await get().configureTypeScriptDefaults(monaco, npmPackages);
                        }

                        // OPTIMIZATION: Don't refresh files immediately - let the next natural refresh handle it
                        // This avoids a second heavy file download right after saving
                        // The file tree will update naturally when user interacts or on next refresh
                        console.log("[IDEStore] Package.json updated successfully - skipping immediate file refresh to avoid heavy operation");
                        
                        // Only update the local state if we have the file in cache
                        if (currentFiles.length > 0) {
                            const packageJsonFile = currentFiles.find(file => file.name.endsWith('package.json'));
                            if (packageJsonFile) {
                                // Update the cached file content
                                const updatedFiles = state.files?.map((file: any) => 
                                    file.name === packageJsonFile.name 
                                        ? { ...file, content: packageJsonContent }
                                        : file
                                ) || [];
                                set({ files: updatedFiles });
                                console.log("[IDEStore] Updated package.json in cached file list");
                            }
                        }

                        // Reset flags after successful update
                        set({
                            hasRunInitialPackageUpdate: true,
                            isPackageUpdateForced: false
                        });

                        console.log("[IDEStore] Package.json update completed successfully");

                    } catch (error) {
                        console.error('[IDEStore] Error updating package.json:', error);
                        onError?.('Failed to update package.json');

                        // Reset force flag even on error
                        set({isPackageUpdateForced: false});
                        throw error;
                    }
                },

                refreshTypeDefinitions: async () => {
                    const state = get();
                    const {monaco, project, editorManager} = state;

                    if (!monaco || !project || !editorManager || !project.npmPackages) {
                        return;
                    }

                    try {
                        console.log('[IDEStore] Refreshing type definitions');

                        // Get the NPM packages
                        const npmPackages = project.npmPackages[0] || [];

                        // Configure TypeScript with the updated packages
                        await get().configureTypeScriptDefaults(monaco, npmPackages);

                        // Mark as refreshed
                        set({needsTypeDefinitionRefresh: false});
                    } catch (error) {
                        console.error('[IDEStore] Error refreshing type definitions:', error);
                    }
                },

                savePackageJSON: async (content, isInitialLoad = false, packageJsonPath?: string) => {
                    const state = get();
                    const {userActor, userPrincipal, project, onError, currentVersionId, cachedPackageJsonPath} = state;

                    if (!userPrincipal || !userActor) {
                        console.log("[IDEStore] Missing userPrincipal or actor");
                        return;
                    }

                    if (!project?.id || project.id === 'none' || !project.name) {
                        console.log("[IDEStore] Invalid project state:", {id: project?.id, name: project?.name});
                        return;
                    }

                    try {
                        console.log(`[IDEStore] Saving package.json with version:`, currentVersionId);

                        // Default location if no package.json is found
                        const defaultDir = project.name + '/src/frontend';
                        const fileName = 'package.json';

                        // Format version parameter for Candid
                        const versionParam = currentVersionId ? [currentVersionId] : [];

                        // OPTIMIZATION: Use provided path, cached path, or find in cached files - avoid backend fetch
                        let packageJsonFile: { fileName: string; path: string } | null = null;
                        
                        // Priority 1: Use provided path (fastest)
                        if (packageJsonPath) {
                            packageJsonFile = {
                                fileName: fileName,
                                path: packageJsonPath
                            };
                            console.log(`[IDEStore] Using provided package.json path: ${packageJsonPath}`);
                        }
                        // Priority 2: Use cached path from state
                        else if (cachedPackageJsonPath) {
                            packageJsonFile = {
                                fileName: fileName,
                                path: cachedPackageJsonPath
                            };
                            console.log(`[IDEStore] Using cached package.json path: ${cachedPackageJsonPath}`);
                        }
                        // Priority 3: Find in cached files (no network call)
                        else {
                            const stateFiles = get().files;
                            if (stateFiles && stateFiles.length > 0) {
                                const foundFile = stateFiles.find((file: any) => 
                                    file.fileName === fileName || file.name.endsWith('/' + fileName)
                                );
                                
                                if (foundFile) {
                                    const pathParts = foundFile.name.split('/');
                                    const filePath = pathParts.slice(0, -1).join('/') || defaultDir;
                                    packageJsonFile = {
                                        fileName: fileName,
                                        path: filePath
                                    };
                                    // Cache the path for future use
                                    set({ cachedPackageJsonPath: filePath });
                                    console.log(`[IDEStore] Found package.json in cached files at ${filePath}`);
                                }
                            }
                        }
                        
                        // LAST RESORT: Only fetch from backend if we truly don't know where it is
                        if (!packageJsonFile) {
                            console.log(`[IDEStore] package.json location unknown, fetching from backend (this should be rare)...`);
                            const existingFiles = await get().safeGetProjectFiles(project.id, versionParam);

                            if (!('ok' in existingFiles)) {
                                throw new Error(existingFiles.err || 'Failed to fetch project files');
                            }

                            // Look for any package.json file in the project
                            const foundFile = existingFiles.ok.find(file => file.fileName === fileName);
                            if (foundFile) {
                                packageJsonFile = {
                                    fileName: fileName,
                                    path: foundFile.path
                                };
                                // Cache the path for future use
                                set({ cachedPackageJsonPath: foundFile.path });
                            }
                        }

                        let result;
                        if (packageJsonFile) {
                            // Update the existing package.json wherever it is
                            console.log(`[IDEStore] Updating existing package.json at ${packageJsonFile.path}`);
                            result = await userActor.updateCodeArtifact(
                                userPrincipal,
                                project.id,
                                fileName,
                                {Text: content},
                                packageJsonFile.path,
                                versionParam
                            );
                        } else {
                            // Create new package.json at the default location
                            console.log(`[IDEStore] Creating new package.json at ${defaultDir}`);
                            result = await userActor.createCodeArtifact(
                                userPrincipal,
                                project.id,
                                fileName,
                                {Text: content},
                                'application/json',
                                'json',
                                defaultDir,
                                versionParam
                            );
                        }

                        if (!('ok' in result)) {
                            throw new Error(result.err || 'Failed to save package.json');
                        }
                        
                        // Mark as recently saved to prevent overwrite by updatePackageFile
                        set({ packageJsonLastSaved: Date.now() });
                        
                        // Parse the saved package.json and sync dependencies to project (non-blocking)
                        // This ensures manually added dependencies are preserved
                        // Run asynchronously so it doesn't slow down the save operation
                        Promise.resolve().then(async () => {
                            try {
                                const parsedJson = JSON.parse(content);
                                const allDependencies = {
                                    ...parsedJson.dependencies || {},
                                    ...parsedJson.devDependencies || {},
                                    ...parsedJson.peerDependencies || {}
                                };
                                
                                const currentPackages = project.npmPackages?.[0] || [];
                                let needsSync = false;
                                
                                // Quick check: are there dependencies in package.json not in project?
                                for (const [name, version] of Object.entries(allDependencies)) {
                                    const exists = currentPackages.some(pkg => pkg.name === name);
                                    if (!exists) {
                                        needsSync = true;
                                        break;
                                    }
                                }
                                
                                if (needsSync) {
                                    console.log("[IDEStore] Syncing manually added dependencies from package.json to project (background)");
                                    // Sync in background - doesn't block the save operation
                                    // Use a small delay to ensure save completes first
                                    setTimeout(() => {
                                        get().parsePackageJson().catch(err => {
                                            console.warn("[IDEStore] Failed to sync dependencies after save:", err);
                                        });
                                    }, 500); // Reduced delay for faster sync
                                }
                            } catch (parseError) {
                                console.warn("[IDEStore] Failed to parse saved package.json for sync:", parseError);
                            }
                        });
                    } catch (err) {
                        const errorMessage = err instanceof Error ? err.message : 'Failed to save package.json';
                        console.error('[IDEStore] Error saving package.json:', err);
                        onError?.(errorMessage);
                        throw new Error(errorMessage);
                    }
                },

                saveMopsToml: async (content, isInitialLoad = false) => {
                    const state = get();
                    const {userActor, userPrincipal, project, onError, currentVersionId} = state;

                    if (!userPrincipal || !userActor) {
                        console.log("[IDEStore] Missing userPrincipal or actor");
                        return;
                    }

                    if (!project?.id || project.id === 'none' || !project.name) {
                        console.log("[IDEStore] Invalid project state:", {id: project?.id, name: project?.name});
                        return;
                    }

                    try {
                        console.log(`[IDEStore] Saving mops.toml with version:`, currentVersionId);

                        // Default location if no mops.toml is found
                        const defaultDir = project.name + '/';
                        const fileName = 'mops.toml';

                        // Format version parameter for Candid
                        const versionParam = currentVersionId ? [currentVersionId] : [];

                        // Get all project files to search for any mops.toml
                        const existingFiles = await get().safeGetProjectFiles(project.id, versionParam);

                        if (!('ok' in existingFiles)) {
                            throw new Error(existingFiles.err || 'Failed to fetch project files');
                        }

                        // Look for any mops.toml file in the project
                        const mopsTomlFile = existingFiles.ok.find(file => file.fileName === fileName);

                        let result;
                        if (mopsTomlFile) {
                            // Update the existing mops.toml wherever it is
                            console.log(`[IDEStore] Updating existing mops.toml at ${mopsTomlFile.path}`);
                            result = await userActor.updateCodeArtifact(
                                userPrincipal,
                                project.id,
                                fileName,
                                {Text: content},
                                mopsTomlFile.path,
                                versionParam
                            );
                        } else {
                            // Create new mops.toml at the default location
                            console.log(`[IDEStore] Creating new mops.toml at ${defaultDir}`);
                            result = await userActor.createCodeArtifact(
                                userPrincipal,
                                project.id,
                                fileName,
                                {Text: content},
                                'text/plain',  // TOML mimetype
                                'plaintext',   // Monaco language identifier
                                defaultDir,
                                versionParam
                            );
                        }

                        if (!('ok' in result)) {
                            throw new Error(result.err || 'Failed to save mops.toml');
                        }
                    } catch (err) {
                        const errorMessage = err instanceof Error ? err.message : 'Failed to save mops.toml';
                        console.error('[IDEStore] Error saving mops.toml:', err);
                        onError?.(errorMessage);
                        throw new Error(errorMessage);
                    }
                },


                updateMopsTomlFile: async () => {
                    const state = get();
                    const {project, userPrincipal, userActor, onError, currentVersionId} = state;

                    if (!project.motokoPackages || project.id === 'none' || !project.name || state.hasRunInitialMopsTomlUpdate) {
                        return;
                    }

                    try {
                        console.log("[IDEStore] Running mops.toml update for version:", currentVersionId);
                        const generator = new MopsTOMLGenerator(project);
                        const mopsTomlContent = generator.generateMopsToml();

                        // Save the mops.toml file
                        await get().saveMopsToml(mopsTomlContent, true);

                        // Refresh files to include the new mops.toml
                        if (userPrincipal && userActor && project.id) {
                            try {
                                const versionParam = currentVersionId ? [currentVersionId] : [];

                                const filesResult = await get().safeGetProjectFiles(
                                    project.id,
                                    versionParam
                                );

                                if ('ok' in filesResult) {
                                    const projectFiles = filesResult.ok.map((file: any) => ({
                                        name: pathUtils.joinPaths(file.path, file.fileName),
                                        content: file.content?.[0]?.Text || '',
                                        fullId: file.id
                                    }));

                                    set({
                                        files: projectFiles,
                                        fileTree: buildFileTree(projectFiles),
                                        hasRunInitialMopsTomlUpdate: true
                                    });
                                    console.log("[IDEStore] File list refreshed after mops.toml update for version:", currentVersionId);
                                }
                            } catch (refreshError) {
                                console.error("[IDEStore] Error refreshing files after mops.toml update:", refreshError);
                            }
                        }
                    } catch (error) {
                        console.error('[IDEStore] Error updating mops.toml:', error);
                        onError?.('Failed to update mops.toml');
                    }
                },

                // Add to your store
                parseMopsToml: async () => {
                    const state = get();
                    const {userActor, userPrincipal, project, currentVersionId} = state;

                    if (!userPrincipal || !userActor || !project?.id) {
                        return;
                    }

                    try {
                        // Check if mops.toml exists
                        const versionParam = currentVersionId ? [currentVersionId] : [];
                        const files = await get().safeGetProjectFiles(project.id, versionParam);

                        if ('ok' in files) {
                            // Look for mops.toml file
                            const mopsTomlFile = files.ok.find(file => file.fileName === 'mops.toml');

                            if (mopsTomlFile && mopsTomlFile.content && typeof mopsTomlFile.content[0]?.Text === 'string') {
                                const tomlContent = mopsTomlFile.content[0].Text;

                                // Parse TOML content
                                const dependencies = parseDependenciesFromToml(tomlContent);

                                // Get current packages
                                const currentPackages = project.motokoPackages?.[0] || [];

                                // Find packages to add (in TOML but not in project)
                                for (const [name, version] of Object.entries(dependencies)) {
                                    const exists = currentPackages.some(pkg => pkg.name === name);

                                    if (!exists) {
                                        // Add package to project
                                        console.log(`Adding package from mops.toml: ${name}@${version}`);
                                        await state.addMotokoPackage({
                                            name,
                                            version: version as string,
                                            repo: '', // You might need to fetch this or use a default
                                            dir: [],  // Optional fields
                                            homepage: []
                                        });
                                    }
                                }
                            }
                        }
                    } catch (error) {
                        console.error('Error parsing mops.toml:', error);
                    }
                },

                parsePackageJson: async () => {
                    const state = get();
                    const {userActor, userPrincipal, project, currentVersionId, onError} = state;

                    if (!userPrincipal || !userActor || !project?.id) {
                        return;
                    }

                    try {
                        console.log('[IDEStore] Checking for existing package.json file');

                        // Check if package.json exists
                        const versionParam = currentVersionId ? [currentVersionId] : [];
                        const filesResult = await get().safeGetProjectFiles(project.id, versionParam);

                        if (!('ok' in filesResult)) {
                            console.log('[IDEStore] Failed to get project files to check for package.json');
                            return;
                        }

                        // Look for package.json file, prioritizing frontend directory but checking project root too
                        let packageJsonFile = filesResult.ok.find(file =>
                            file.fileName === 'package.json' &&
                            (file.path.includes('/frontend') || !file.path.includes('/src/'))
                        );

                        if (!packageJsonFile || !packageJsonFile.content) {
                            console.log('[IDEStore] No package.json file found');
                            return;
                        }

                        // Extract content
                        let packageJsonContent = '';
                        if (typeof packageJsonFile.content === 'string') {
                            packageJsonContent = packageJsonFile.content;
                        } else if (Array.isArray(packageJsonFile.content) && packageJsonFile.content.length > 0) {
                            const contentItem = packageJsonFile.content[0];
                            if (contentItem && typeof contentItem === 'object' && 'Text' in contentItem) {
                                packageJsonContent = contentItem.Text;
                            }
                        } else if (packageJsonFile.content && typeof packageJsonFile.content === 'object' && 'Text' in packageJsonFile.content) {
                            packageJsonContent = packageJsonFile.content.Text;
                        }

                        if (!packageJsonContent) {
                            console.log('[IDEStore] package.json file exists but content could not be extracted');
                            return;
                        }

                        console.log('[IDEStore] Found package.json file, parsing dependencies');

                        // Parse JSON content
                        let packageJson;
                        try {
                            packageJson = JSON.parse(packageJsonContent);
                        } catch (parseError) {
                            console.error('[IDEStore] Failed to parse package.json:', parseError);
                            return;
                        }

                        // Extract dependencies from different sections
                        const allDependencies = {
                            ...packageJson.dependencies || {},
                            ...packageJson.devDependencies || {},
                            ...packageJson.peerDependencies || {}
                        };

                        console.log('[IDEStore] Parsed dependencies:', allDependencies);

                        // Get current packages
                        const currentPackages = project.npmPackages?.[0] || [];

                        // Track if we added any packages
                        let packagesAdded = false;

                        // Find packages to add (in package.json but not in project)
                        for (const [name, version] of Object.entries(allDependencies)) {
                            const exists = currentPackages.some(pkg => pkg.name === name);

                            if (!exists) {
                                // Determine dependency type
                                let dependencyType = 'dependencies';
                                if (packageJson.devDependencies && name in packageJson.devDependencies) {
                                    dependencyType = 'devDependencies';
                                } else if (packageJson.peerDependencies && name in packageJson.peerDependencies) {
                                    dependencyType = 'peerDependencies';
                                }

                                // Add package to project
                                console.log(`[IDEStore] Adding package from package.json: ${name}@${version} (${dependencyType})`);

                                try {
                                    await state.addNPMPackage({
                                        name,
                                        version: version as string,
                                        dependencyType
                                    });

                                    packagesAdded = true;
                                } catch (packageError) {
                                    console.error(`[IDEStore] Error adding package ${name}:`, packageError);
                                }
                            }
                        }

                        if (packagesAdded) {
                            console.log('[IDEStore] Successfully imported packages from package.json');

                            // Force project update if packages were added
                            if (state.onProjectUpdate) {
                                await state.onProjectUpdate();
                            }
                        } else {
                            console.log('[IDEStore] No new packages found in package.json');
                        }

                    } catch (error) {
                        console.error('[IDEStore] Error parsing package.json:', error);
                        onError?.(`Error importing packages from package.json: ${error instanceof Error ? error.message : String(error)}`);
                    }
                },

                configureTypeScriptDefaults: async (monacoInstance, npmPackages) => {
                    if (!monacoInstance.languages.typescript) {
                        console.warn('[IDEStore] TypeScript language service not available');
                        return;
                    }

                    const state = get();
                    const {project, isConfiguringTypeScript} = state;

                    // Prevent cascading: check if we're already configuring
                    if (isConfiguringTypeScript) {
                        console.log('[IDEStore] TypeScript configuration already in progress, skipping duplicate call');
                        return;
                    }

                    // Create a stable signature of packages to detect meaningful changes
                    const packagesSignature = JSON.stringify(
                        npmPackages.map(p => ({ name: p.name, version: p.version })).sort((a, b) => a.name.localeCompare(b.name))
                    );
                    const previousSignature = state.lastTypeScriptConfigSignature || '';

                    // Prevent cascading: only configure if packages actually changed
                    if (previousSignature === packagesSignature && previousSignature !== '') {
                        console.log('[IDEStore] TypeScript packages unchanged, skipping configuration');
                        return;
                    }

                    // Mark as configuring to prevent cascading
                    set({ 
                        isConfiguringTypeScript: true,
                        lastTypeScriptConfigSignature: packagesSignature
                    });

                    try {
                        // Set compiler options
                        const compilerOptions: Monaco.languages.typescript.CompilerOptions = {
                            target: monacoInstance.languages.typescript.ScriptTarget.ES2020,
                            allowNonTsExtensions: true,
                            moduleResolution: monacoInstance.languages.typescript.ModuleResolutionKind.NodeJs,
                            module: monacoInstance.languages.typescript.ModuleKind.ESNext,
                            noEmit: true,
                            esModuleInterop: true,
                            jsx: monacoInstance.languages.typescript.JsxEmit.React,
                            reactNamespace: 'React',
                            allowJs: true,
                            typeRoots: ["node_modules/@types"],
                            skipLibCheck: true,
                            strict: false
                        };

                        monacoInstance.languages.typescript.typescriptDefaults.setCompilerOptions(compilerOptions);
                        monacoInstance.languages.typescript.typescriptDefaults.setExtraLibs([]);

                        // Initialize TypeDefinitionService if needed
                        let jsTypeDefinitionService = state.jsTypeDefinitionService;
                        if (!jsTypeDefinitionService || jsTypeDefinitionService.getProject().id !== project.id) {
                            jsTypeDefinitionService = new JSTypeDefinitionService(monacoInstance, project);
                            set({jsTypeDefinitionService});
                        }

                        // Add type definitions for all packages
                        for (const pkg of npmPackages) {
                            try {
                                // Now uses fetchTypeDefinitions (restored) with in-flight request tracking
                                const types = await jsTypeDefinitionService.getTypeDefinition(pkg.name);
                                if (types) {
                                    const filePath = `file:///node_modules/${pkg.name}/index.d.ts`;
                                    monacoInstance.languages.typescript.typescriptDefaults.addExtraLib(
                                        types,
                                        filePath
                                    );
                                }
                            } catch (pkgError) {
                                console.warn(`[IDEStore] Error loading types for ${pkg.name}:`, pkgError);
                            }
                        }

                        set({needsTypeDefinitionRefresh: false});
                    } catch (error) {
                        console.error('[IDEStore] Error configuring TypeScript defaults:', error);
                    } finally {
                        // Always clear the flag, even on error
                        set({isConfiguringTypeScript: false});
                    }
                },


                setConsoleTab: (tab: 'console' | 'ai') => {
                    set({activeConsoleTab: tab});
                },

                toggleClaudePanel: () => {
                    set(state => ({activeConsoleTab: state.activeConsoleTab === 'console' ? 'ai' : 'console'}));
                },

                toggleMotokoPackagesDialog: () => {
                    set(state => ({isMotokoPackagesDialogOpen: !state.isMotokoPackagesDialogOpen}));
                },

                toggleNPMPackagesDialog: () => {
                    set(state => ({isNPMPackagesDialogOpen: !state.isNPMPackagesDialogOpen}));
                },

                setCompilerDiagnostics: (diagnostics) => {
                    set({compilerDiagnostics: diagnostics});
                },

                setDeploymentError: (error) => {
                    set({deploymentError: error});
                },

                hideDialogs: () => {
                    set({
                        showNewFileDialog: false,
                        showNewFolderDialog: false,
                        showDeleteConfirmation: false,
                        showSaveConfirmation: false,
                        showWasmNotFoundDialog: false,
                        showCompilerErrors: false,
                        isMotokoPackagesDialogOpen: false,
                        isNPMPackagesDialogOpen: false
                    });
                },

                deploy: async (deploymentId = null, initArgs = null, deploymentMode = 'upgrade', projectType = 'icpstudio', targetCanisterId = null, selectedActorFile = null) => {
                    const state = get();
                    const {
                        userActor,
                        userPrincipal,
                        identity,
                        files,
                        project,
                        activeType,
                        activeFile,
                        compilerService,
                        compilerReady,
                        userCanisters,
                        onError,
                        onSuccess,
                        originalProjectUpdate,
                        currentVersionId,
                        fileTree,
                        platformActor
                    } = state;

                    console.log("Deploy called with:", {
                        initArgs,
                        deploymentMode,
                        projectType,
                        targetCanisterId,
                        activeFile,
                        activeType
                    });

                    if (!userPrincipal || !userActor) {
                        onError?.('Missing required deployment parameters');
                        return;
                    }

                    // Merge current fileContent into files array before deployment
                    // Only merge if there are unsaved changes to ensure we're not overwriting with stale content
                    let filesForDeploy = [...files];
                    
                    // Only merge active file content if there are unsaved changes
                    // This ensures saved files use backend content, not stale local content
                    if (activeFile && state.fileContent !== undefined && state.hasUnsavedChanges) {
                        const activeFileIndex = filesForDeploy.findIndex(f => f.name === activeFile);
                        if (activeFileIndex >= 0) {
                            // Update the active file's content with current editor content
                            filesForDeploy[activeFileIndex] = {
                                ...filesForDeploy[activeFileIndex],
                                content: state.fileContent
                            };
                            console.log(`[Deploy] Merged unsaved content for active file: ${activeFile} (${state.fileContent.length} chars)`);
                        } else {
                            // File not in array, add it (shouldn't happen but be safe)
                            console.warn(`[Deploy] Active file ${activeFile} not found in files array, adding it`);
                            filesForDeploy.push({
                                name: activeFile,
                                content: state.fileContent,
                                path: pathUtils.getParentPath(activeFile),
                                fileName: pathUtils.getFileName(activeFile)
                            });
                        }
                    } else if (activeFile && !state.hasUnsavedChanges) {
                        console.log(`[Deploy] Active file ${activeFile} has no unsaved changes, using content from files array`);
                    }
                    
                    // Validate files before deployment
                    const getFileContent = (file: any): string => {
                        if (typeof file.content === 'string') {
                            return file.content;
                        }
                        if (file.content && typeof file.content === 'object' && 'Text' in file.content) {
                            return file.content.Text || '';
                        }
                        return '';
                    };
                    
                    const filesWithEmptyContent = filesForDeploy.filter(f => {
                        const content = getFileContent(f);
                        return !content || content.trim() === '';
                    });
                    
                    if (filesWithEmptyContent.length > 0) {
                        console.warn(`[Deploy] WARNING: ${filesWithEmptyContent.length} files have empty content:`, 
                            filesWithEmptyContent.map(f => f.name));
                    }
                    
                    // Log all files being deployed for debugging
                    console.log(`[Deploy] Preparing ${filesForDeploy.length} files for deployment:`, {
                        totalFiles: filesForDeploy.length,
                        filesWithContent: filesForDeploy.filter(f => {
                            const content = getFileContent(f);
                            return content && content.trim().length > 0;
                        }).length,
                        filesWithEmptyContent: filesWithEmptyContent.length,
                        files: filesForDeploy.map(f => {
                            const content = getFileContent(f);
                            return { 
                                name: f.name, 
                                contentLength: content.length,
                                hasContent: !!(content && content.trim().length > 0)
                            };
                        })
                    });
                    
                    // Store current files for post-deployment comparison
                    const currentFiles = [...filesForDeploy];

                    // Common parameters for both backend and frontend
                    const baseParams = {
                        files: filesForDeploy,
                        project,
                        userPrincipal,
                        actor: userActor,
                        userCanisters,
                        onError,
                        onSuccess,
                        onProjectUpdate: originalProjectUpdate,
                        setCompilationStatus: get().setCompilationStatus,
                        setDeploymentError: get().setDeploymentError,
                        deploymentId,
                        versionId: currentVersionId,
                        installMode: deploymentMode
                    };

                    set({compilationStatus: 'compiling'});

                    try {
                        if (activeType === 'backend') {
                            // Initialize compiler if needed
                            if (!compilerReady) {
                                try {
                                    await compilerService.init();
                                    set({compilerReady: true});
                                } catch (error) {
                                    onError?.(`Failed to initialize compiler: ${error instanceof Error ? error.message : String(error)}`);
                                    return;
                                }
                            }

                            // Add backend-specific parameters with initArgs and targetCanisterId
                            const backendParams = {
                                ...baseParams,
                                activeFile: selectedActorFile || activeFile,
                                selectedActorFile: selectedActorFile,
                                compilerService,
                                initArgs,
                                targetCanisterId,
                                identity
                            };

                            console.log("Backend deployment params:", {
                                activeFile: backendParams.activeFile,
                                initArgs: backendParams.initArgs,
                                installMode: backendParams.installMode,
                                targetCanisterId: backendParams.targetCanisterId
                            });

                            try {
                                const deployResult = await handleBackendDeploy(backendParams);
                                let canisterId;
                                if (deployResult && deployResult.success && deployResult.canisterId) {
                                    canisterId = deployResult.canisterId;
                                    console.log(`Successfully deployed to canister: ${canisterId.toString()} using mode: ${deploymentMode}`);

                                    // ANALYTICS: Track successful backend deployment
                                    try {
                                        await trackDeploymentSuccess('backend');
                                        console.log('[Analytics] Tracked successful backend deployment');
                                    } catch (analyticsError) {
                                        console.error('[Analytics] Error tracking backend deployment:', analyticsError);
                                    }
                                }

                                if (currentVersionId && canisterId) {
                                    try {
                                        // Get the current version to access its canisters
                                        const versionResult = await userActor.getProjectVersion(currentVersionId);

                                        if ('ok' in versionResult) {
                                            const version = versionResult.ok;
                                            // Check if this canister is already associated with this version
                                            const canisterExists = version.canisters.some(c =>
                                                c.toString() === canisterId.toString()
                                            );

                                            if (!canisterExists) {
                                                // Add the canister to the version's canisters list
                                                const updatedCanisters = [...version.canisters, canisterId];
                                                await userActor.updateVersionCanisters(currentVersionId, updatedCanisters);

                                                // Create a deployment reference for this version
                                                const deploymentRef = {
                                                    id: `deploy-${Date.now()}`,
                                                    name: `${activeType} deployment (${deploymentMode})`,
                                                    projectId: project.id,
                                                    canisterId: canisterId,
                                                    status: 'deployed',
                                                    network: 'ic',
                                                    lastUpdated: BigInt(Date.now() * 1000000)
                                                };

                                                await userActor.addDeploymentToVersion(currentVersionId, deploymentRef);
                                            }
                                        }
                                    } catch (versionError) {
                                        console.error('Error associating deployment with version:', versionError);
                                    }
                                }

                                // After successful deployment, do an optimized refresh
                                await get().performOptimizedRefreshAfterDeploy(currentFiles, currentVersionId);

                                // Still run the safe project update to ensure canister association
                                await get().safeProjectUpdate();
                            } catch (error: any) {
                                // ANALYTICS: Track failed backend deployment
                                try {
                                    await trackDeploymentFailure('backend');
                                    console.log('[Analytics] Tracked failed backend deployment');
                                } catch (analyticsError) {
                                    console.error('[Analytics] Error tracking failed backend deployment:', analyticsError);
                                }

                                // Using 'any' type for error
                                if (error?.diagnostics) {
                                    set({
                                        compilerDiagnostics: Array.isArray(error.diagnostics) ? error.diagnostics : [error.diagnostics],
                                        showCompilerErrors: true
                                    });
                                } else {
                                    onError?.(error instanceof Error ? error.message : 'Compilation failed');
                                }
                                return;
                            }
                        } else { // Frontend deployment
                            // Add frontend-specific parameters
                            const frontendParams = {
                                ...baseParams,
                                identity,
                                projectType,
                                targetCanisterId  // Also pass targetCanisterId for frontend deployments if needed
                            };

                            console.log("Frontend deployment params:", {
                                projectType: frontendParams.projectType,
                                targetCanisterId: frontendParams.targetCanisterId
                            });

                            try {
                                const deployResult = await handleFrontendDeploy(frontendParams);
                                let canisterId;
                                if (deployResult.success && deployResult.canisterId) {
                                    canisterId = deployResult.canisterId;
                                    console.log(`Successfully deployed to canister: ${canisterId.toString()}`);

                                    // ANALYTICS: Track successful frontend deployment
                                    try {
                                        await trackDeploymentSuccess('frontend');
                                        console.log('[Analytics] Tracked successful frontend deployment');
                                    } catch (analyticsError) {
                                        console.error('[Analytics] Error tracking frontend deployment:', analyticsError);
                                    }
                                }

                                if (currentVersionId && canisterId) {
                                    try {
                                        const versionResult = await userActor.getProjectVersion(currentVersionId);

                                        if ('ok' in versionResult) {
                                            const version = versionResult.ok;
                                            const canisterExists = version.canisters.some(c =>
                                                c.toString() === canisterId.toString()
                                            );

                                            if (!canisterExists) {
                                                const updatedCanisters = [...version.canisters, canisterId];
                                                await userActor.updateVersionCanisters(currentVersionId, updatedCanisters);

                                                const deploymentRef = {
                                                    id: `deploy-${Date.now()}`,
                                                    name: `${activeType} deployment`,
                                                    projectId: project.id,
                                                    canisterId: canisterId,
                                                    status: 'deployed',
                                                    network: 'ic',
                                                    lastUpdated: BigInt(Date.now() * 1000000)
                                                };

                                                await userActor.addDeploymentToVersion(currentVersionId, deploymentRef);
                                            }
                                        }
                                    } catch (versionError) {
                                        console.error('Error associating frontend deployment with version:', versionError);
                                    }
                                }

                                // After successful deployment, do an optimized refresh
                                await get().performOptimizedRefreshAfterDeploy(currentFiles, currentVersionId);

                                // And run the safe project update
                                await get().safeProjectUpdate();
                            } catch (error) {
                                // ANALYTICS: Track failed frontend deployment
                                try {
                                    await trackDeploymentFailure('frontend');
                                    console.log('[Analytics] Tracked failed frontend deployment');
                                } catch (analyticsError) {
                                    console.error('[Analytics] Error tracking failed frontend deployment:', analyticsError);
                                }

                                const errorMessage = error instanceof Error ? error.message : 'Frontend deployment failed';
                                onError?.(errorMessage);
                                return;
                            }
                        }
                    } finally {
                        set({compilationStatus: 'idle'});
                    }
                },


                performOptimizedRefreshAfterDeploy: async (oldFiles, versionId) => {
                    const state = get();
                    const startingProjectId = state.project?.id;
                    const {userActor, userPrincipal, project, fileTree} = state;

                    if (!userActor || !userPrincipal || !project?.id || project.id === 'none') {
                        console.log('[performOptimizedRefreshAfterDeploy] Missing dependencies, aborting');
                        return;
                    }

                    console.log(`[performOptimizedRefreshAfterDeploy] Starting refresh for project: ${startingProjectId}`);

                    try {
                        set({loading: true});

                        // Version parameter in Candid format
                        const versionParam = versionId ? [versionId] : [];

                        // Get fresh files from backend after deployment
                        const filesResult = await get().safeGetProjectFiles(
                            project.id,
                            versionParam,
                            true // forceRefresh = true
                        );

                        // Check if project changed during API call
                        if (get().project?.id !== startingProjectId) {
                            console.log('[performOptimizedRefreshAfterDeploy] Project changed during API call, aborting');
                            return;
                        }

                        if ('ok' in filesResult) {
                            // Transform files preserving exact backend paths
                            const newFiles = enhancedTransformFiles(filesResult.ok);

                            // Check if project changed during transformation
                            if (get().project?.id !== startingProjectId) {
                                console.log('[performOptimizedRefreshAfterDeploy] Project changed during transformation, aborting');
                                return;
                            }

                            // Check if file structure changed using exact path comparison
                            const structureChanged = get().isFileStructureChanged(oldFiles, newFiles);

                            if (structureChanged) {
                                // Only rebuild tree if structure changed
                                console.log('[performOptimizedRefreshAfterDeploy] File structure changed, rebuilding tree');
                                const newFileTree = buildFileTree(newFiles);

                                // Final check before state update
                                if (get().project?.id !== startingProjectId) {
                                    console.log('[performOptimizedRefreshAfterDeploy] Project changed before state update, aborting');
                                    return;
                                }

                                set({
                                    files: newFiles,
                                    fileTree: newFileTree,
                                    loading: false
                                });
                            } else {
                                // Just update files array if structure is the same
                                console.log('[performOptimizedRefreshAfterDeploy] File structure unchanged, keeping tree reference');

                                // Final check before state update
                                if (get().project?.id !== startingProjectId) {
                                    console.log('[performOptimizedRefreshAfterDeploy] Project changed before state update, aborting');
                                    return;
                                }

                                set({
                                    files: newFiles,
                                    loading: false
                                });
                            }

                            console.log(`[performOptimizedRefreshAfterDeploy] Successfully completed refresh for project: ${startingProjectId}`);
                        }
                    } catch (error) {
                        console.error('[performOptimizedRefreshAfterDeploy] Error refreshing after deployment:', error);
                    } finally {
                        // Only clear loading if still on same project
                        if (get().project?.id === startingProjectId) {
                            set({loading: false});
                        }
                    }
                },

                downloadWasm: async () => {
                    const state = get();
                    const { userActor, userPrincipal, project, onError, currentVersionId } = state;

                    console.log('[IDEStore] Starting WASM discovery');

                    if (!userPrincipal || !userActor || !project?.id) {
                        onError?.('Missing required parameters');
                        return;
                    }

                    try {
                        // Version parameter in Candid format
                        const versionParam = currentVersionId ? [currentVersionId] : [];

                        // First, search for all WASM files in the project
                        const filesResult = await get().safeGetProjectFiles(
                            project.id,
                            versionParam
                        );

                        if (!('ok' in filesResult)) {
                            throw new Error(`Failed to get project files: ${filesResult.err}`);
                        }

                        // Find all WASM files in the project
                        const wasmFiles = filesResult.ok
                            .filter(file => file.fileName.endsWith('.wasm'))
                            .map(file => ({
                                path: file.path,
                                fileName: file.fileName,
                                size: file.size || 0,
                                lastModified: file.lastModified || new Date().toISOString(),
                                displayName: `${file.path}/${file.fileName}`,
                                actorName: file.fileName.replace('.wasm', '')
                            }));

                        console.log(`[IDEStore] Found ${wasmFiles.length} WASM files:`, wasmFiles);

                        if (wasmFiles.length === 0) {
                            console.log("[IDEStore] No WASM files found in project");
                            set({ showWasmNotFoundDialog: true });
                            return;
                        }

                        if (wasmFiles.length === 1) {
                            // If only one WASM file, download it directly
                            console.log("[IDEStore] Only one WASM file found, downloading directly");
                            await get().downloadSelectedWasm(wasmFiles[0]);
                        } else {
                            // Multiple WASM files found, show selection dialog
                            console.log("[IDEStore] Multiple WASM files found, showing selection dialog");
                            set({
                                availableWasmFiles: wasmFiles,
                                showWasmSelectionDialog: true
                            });
                        }

                    } catch (error) {
                        console.error("[IDEStore] Error in downloadWasm:", error);
                        onError?.(error instanceof Error ? error.message : 'Failed to discover WASM files');
                    }
                },

                downloadSelectedWasm: async (wasmFile) => {
                    const state = get();
                    const { userActor, userPrincipal, project, onSuccess, onError, currentVersionId } = state;

                    if (!userPrincipal || !userActor || !project?.id) {
                        onError?.('Missing required parameters');
                        return;
                    }

                    try {
                        console.log(`[IDEStore] Downloading WASM: ${wasmFile.path}/${wasmFile.fileName}`);

                        // Version parameter in Candid format
                        const versionParam = currentVersionId ? [currentVersionId] : [];

                        // Helper function for chunked download (same as before)
                        const downloadLargeWasm = async (path: string, fileName: string) => {
                            console.log(`[IDEStore] Starting chunked download for ${path}/${fileName}`);

                            try {
                                const sessionResult = await userActor.startWasmDownloadSession(
                                    project.id,
                                    path,
                                    fileName
                                );

                                if (!('ok' in sessionResult)) {
                                    throw new Error(`Failed to start download session: ${sessionResult.err}`);
                                }

                                const sessionId = sessionResult.ok.sessionId;
                                const totalChunks = Number(sessionResult.ok.totalChunks);
                                const totalSize = Number(sessionResult.ok.totalSize);
                                const chunkSize = Number(sessionResult.ok.chunkSize || 0);

                                console.log(`[IDEStore] Download session created with ID: ${sessionId}`);
                                console.log(`[IDEStore] File size: ${totalSize} bytes, chunks: ${totalChunks}, chunk size: ${chunkSize}`);

                                const fullBuffer = new Uint8Array(totalSize);
                                let offset = 0;

                                for (let i = 0; i < totalChunks; i++) {
                                    console.log(`[IDEStore] Downloading chunk ${i + 1}/${totalChunks}`);

                                    const chunkResult = await userActor.downloadWasmChunk(sessionId, i);

                                    if (!('ok' in chunkResult)) {
                                        throw new Error(`Failed to download chunk ${i}: ${chunkResult.err}`);
                                    }

                                    const rawChunkData = chunkResult.ok;
                                    const chunkData = new Uint8Array(
                                        Array.isArray(rawChunkData)
                                            ? rawChunkData.map(val => typeof val === 'bigint' ? Number(val) : val)
                                            : rawChunkData
                                    );

                                    fullBuffer.set(chunkData, offset);
                                    offset += chunkData.length;

                                    console.log(`[IDEStore] Chunk ${i + 1} downloaded (${chunkData.length} bytes), total: ${offset}/${totalSize}`);
                                }

                                await userActor.cleanupWasmDownloadSession(sessionId)
                                    .catch(err => console.warn("[IDEStore] Failed to cleanup session:", err));

                                console.log("[IDEStore] Chunked download completed successfully");
                                return fullBuffer;
                            } catch (error) {
                                console.error("[IDEStore] Error in chunked download:", error);
                                throw error;
                            }
                        };

                        // Helper function to handle download with data
                        const handleDownloadWithData = (binaryData: Uint8Array, filename: string) => {
                            const blob = new Blob([binaryData], { type: 'application/wasm' });
                            const url = URL.createObjectURL(blob);

                            const a = document.createElement('a');
                            a.href = url;
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();

                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);

                            onSuccess?.(`WASM file '${filename}' downloaded successfully`);
                            return true;
                        };

                        // Try direct download first
                        try {
                            const result = await userActor.readCodeArtifact(
                                userPrincipal,
                                project.id,
                                wasmFile.path,
                                wasmFile.fileName,
                                versionParam
                            );

                            if ('ok' in result && result.ok.content?.[0] && 'Binary' in result.ok.content[0]) {
                                console.log(`[IDEStore] Successfully downloaded WASM directly: ${wasmFile.fileName}`);
                                const binaryData = new Uint8Array(result.ok.content[0].Binary);
                                handleDownloadWithData(binaryData, wasmFile.fileName);
                                return;
                            } else {
                                throw new Error("No binary content found");
                            }
                        } catch (directError) {
                            // Check if this is a payload size error
                            if ((directError as Error).message &&
                                (directError as Error).message.includes('payload size') &&
                                (directError as Error).message.includes('cannot be larger than')) {

                                console.log(`[IDEStore] WASM file too large for direct download, trying chunked download`);

                                try {
                                    const binaryData = await downloadLargeWasm(wasmFile.path, wasmFile.fileName);
                                    handleDownloadWithData(binaryData, wasmFile.fileName);
                                    return;
                                } catch (chunkError) {
                                    console.error(`[IDEStore] Chunked download failed:`, chunkError);
                                    throw chunkError;
                                }
                            } else {
                                console.error(`[IDEStore] Direct download error:`, directError);
                                throw directError;
                            }
                        }

                    } catch (error) {
                        console.error("[IDEStore] Error downloading WASM file:", error);
                        onError?.(error instanceof Error ? error.message : `Failed to download ${wasmFile.fileName}`);
                    } finally {
                        // Hide the selection dialog
                        set({ showWasmSelectionDialog: false });
                    }
                },

                handlePayloadSizeError: (error: any) => {
                    const errorMessage = error instanceof Error ? error.message : String(error);

                    // Check if this is a payload size error
                    if (errorMessage.includes('payload size') &&
                        errorMessage.includes('cannot be larger than') &&
                        errorMessage.includes('2097152')) {

                        console.log('[IDE] Detected payload size error, showing WASM cleanup dialog');

                        // Show the payload size error dialog instead of the regular error
                        set({
                            showPayloadSizeErrorDialog: true,
                            payloadSizeErrorMessage: errorMessage
                        });

                        return true; // Indicates we handled this error
                    }

                    return false; // Not a payload size error, let normal error handling proceed
                },


                downloadProjectFilesBatched: async (projectId: string, versionParam: any[]) => {
                    const state = get();
                    const { userActor, userPrincipal, isDownloadingFiles, activeDownloadPromise, activeDownloadKey } = state;

                    if (!userActor || !userPrincipal) {
                        throw new Error('Missing required dependencies for batch download');
                    }

                    // Create cache key for this request
                    const cacheKey = `${projectId}-${JSON.stringify(versionParam)}`;

                    // If a download is already in progress for the SAME project/version, reuse the promise
                    if (isDownloadingFiles && activeDownloadPromise && activeDownloadKey === cacheKey) {
                        console.log(`[IDE] Reusing existing download promise for ${cacheKey}`);
                        try {
                            return await activeDownloadPromise;
                        } catch (error) {
                            console.warn(`[IDE] Existing download promise failed, starting new download`);
                            // Fall through to start a new download
                        }
                    }

                    // If a download is in progress for a DIFFERENT project/version, wait briefly
                    if (isDownloadingFiles && activeDownloadKey !== cacheKey) {
                        console.log(`[IDE] Different download in progress (${activeDownloadKey}), waiting briefly...`);
                        await new Promise(resolve => setTimeout(resolve, 500));
                        // Retry - might be done now
                        if (!get().isDownloadingFiles) {
                            return await get().downloadProjectFilesBatched(projectId, versionParam);
                        }
                    }

                    // Mark as downloading
                    set({ isDownloadingFiles: true, activeDownloadKey: cacheKey });

                    // Create shared promise for this download
                    const downloadPromise = (async () => {
                        try {
                            console.log(`[IDE] Starting batch download for project ${projectId}`);

                        // Step 1: Start the download session
                        const sessionResult = await userActor.startProjectFilesDownloadSession(
                            userPrincipal,
                            projectId,
                            versionParam.length > 0 ? versionParam : []
                        );

                        if (!('ok' in sessionResult)) {
                            throw new Error(`Failed to start download session: ${sessionResult.err}`);
                        }

                        const session = sessionResult.ok;
                        // Convert BigInt to number for calculations
                        const totalBatches = typeof session.totalBatches === 'bigint' 
                            ? Number(session.totalBatches) 
                            : session.totalBatches;
                        const totalFiles = typeof session.totalFiles === 'bigint'
                            ? Number(session.totalFiles)
                            : session.totalFiles;
                        
                        console.log(`[IDE] Download session started: ${totalFiles} files in ${totalBatches} batches`);

                        // Step 2: Download all batches in parallel for maximum performance
                        console.log(`[IDE] Starting parallel download of ${totalBatches} batches...`);
                        
                        // Download batches in parallel with controlled concurrency
                        const CONCURRENT_BATCHES = 5; // Download 5 batches concurrently
                        const batchResults: any[] = new Array(totalBatches);
                        let lastBatchIndex = -1;

                        // Helper to download a single batch
                        const downloadBatch = async (batchIndex: number) => {
                            const batchResult = await userActor.downloadProjectFilesBatch(
                                session.sessionId,
                                batchIndex
                            );

                            if (!('ok' in batchResult)) {
                                throw new Error(`Failed to download batch ${batchIndex}: ${batchResult.err}`);
                            }

                            const batch = batchResult.ok;
                            batchResults[batchIndex] = batch;
                            
                            if (batch.isLastBatch) {
                                lastBatchIndex = batchIndex;
                            }
                            
                            console.log(`[IDE] Batch ${batchIndex + 1}/${session.totalBatches} downloaded: ${batch.files.length} files`);
                            return { index: batchIndex, batch };
                        };

                        // Download batches in chunks with controlled concurrency
                        const downloadChunk = async (startIndex: number, endIndex: number) => {
                            const promises = [];
                            for (let i = startIndex; i < endIndex; i++) {
                                promises.push(downloadBatch(i));
                            }
                            return Promise.all(promises);
                        };

                        // Process in chunks
                        for (let i = 0; i < totalBatches; i += CONCURRENT_BATCHES) {
                            const endIndex = Math.min(i + CONCURRENT_BATCHES, totalBatches);
                            await downloadChunk(i, endIndex);
                        }
                        
                        // Combine all files in order
                        let allFiles: any[] = [];
                        const maxIndex = lastBatchIndex >= 0 ? lastBatchIndex + 1 : totalBatches;
                        
                        for (let i = 0; i < maxIndex; i++) {
                            if (batchResults[i] && batchResults[i].files) {
                                allFiles = allFiles.concat(batchResults[i].files);
                            }
                        }
                        
                        console.log(`[IDE] All ${totalBatches} batches downloaded in parallel: ${allFiles.length} total files`);

                        // Step 3: Cleanup the session
                        try {
                            await userActor.cleanupProjectFilesDownloadSession(session.sessionId);
                            console.log(`[IDE] Download session cleaned up`);
                        } catch (cleanupError) {
                            console.warn(`[IDE] Failed to cleanup download session:`, cleanupError);
                            // Don't fail the whole operation for cleanup errors
                        }

                        console.log(`[IDE] Batch download completed successfully: ${allFiles.length} files`);

                        // Return in the same format as the original getProjectFiles
                        return { ok: allFiles };
                        } catch (error) {
                            console.error('[IDE] Error in batch download:', error);
                            throw error;
                        }
                    })();

                    // Store the promise so other requests can reuse it
                    set({ activeDownloadPromise: downloadPromise });

                    try {
                        const result = await downloadPromise;
                        return result;
                    } finally {
                        // Always clear the flags, even on error
                        set({ 
                            isDownloadingFiles: false,
                            activeDownloadPromise: null,
                            activeDownloadKey: null
                        });
                    }
                },

                safeGetProjectFiles: async (projectId: string, versionParam: any[], forceRefresh: boolean = false) => {
                    const state = get();

                    try {
                        const result = await state.userActor.getProjectFiles(
                            state.userPrincipal,
                            projectId,
                            versionParam
                        );
                        
                        return result;
                    } catch (error) {
                        const errorMessage = error instanceof Error ? error.message : String(error);

                        // Check if this is a payload size error (browser-level)
                        if (errorMessage.includes('payload size') &&
                            errorMessage.includes('cannot be larger than') &&
                            errorMessage.includes('2097152')) {

                            console.log('[IDE] Detected payload size error from browser, switching to batch mode automatically');

                            // Automatically switch to batch download without showing error dialog
                            try {
                                return await get().downloadProjectFilesBatched(projectId, versionParam);
                            } catch (batchError) {
                                const batchErrorMessage = batchError instanceof Error ? batchError.message : String(batchError);
                                console.error('[IDE] Batch download also failed:', batchError);
                                
                                // If it's just a "download in progress" error, wait and retry once
                                if (batchErrorMessage.includes('Download already in progress')) {
                                    console.log('[IDE] Download in progress, waiting and retrying...');
                                    await new Promise(resolve => setTimeout(resolve, 1000));
                                    try {
                                        return await get().downloadProjectFilesBatched(projectId, versionParam);
                                    } catch (retryError) {
                                        console.error('[IDE] Batch download retry also failed:', retryError);
                                    }
                                }
                                
                                // If batch download fails after retry, then show the payload size error dialog
                                set({
                                    showPayloadSizeErrorDialog: true,
                                    payloadSizeErrorMessage: errorMessage
                                });
                                return { err: "Project too large - please delete large WASM files" };
                            }
                        }

                        // Handle other types of errors normally
                        throw error;
                    }
                },

                showWasmSelection: () => {
                    set({ showWasmSelectionDialog: true });
                },

                hideWasmSelection: () => {
                    set({
                        showWasmSelectionDialog: false,
                        availableWasmFiles: []
                    });
                },


                updateFileInTree: (filePath, newContent) => {
                    const state = get();
                    const {files, fileTree} = state;

                    // Check if file exists first
                    const fileExists = files.some(f => f.name === filePath);
                    if (!fileExists) {
                        console.warn(`[IDEStore] Attempted to update non-existent file: ${filePath}`);
                        return {updatedFiles: files, updatedFileTree: fileTree};
                    }

                    // Update the file in the files array
                    const updatedFiles = files.map(file =>
                        file.name === filePath
                            ? {...file, content: newContent}
                            : file
                    );

                    // The file structure hasn't changed (no files added/removed)
                    // so we can keep the same fileTree reference
                    set({
                        files: updatedFiles
                    });

                    return {
                        updatedFiles,
                        updatedFileTree: fileTree
                    };
                },

                isFileStructureChanged: (oldFiles, newFiles) => {
                    // If lengths differ, structure has changed
                    if (oldFiles.length !== newFiles.length) return true;

                    // Create sorted arrays of file paths for comparison
                    // Use exact file.name without normalization for path comparisons
                    const oldPaths = oldFiles.map(f => f.name).sort();
                    const newPaths = newFiles.map(f => f.name).sort();

                    // Check if any paths differ using exact comparison
                    for (let i = 0; i < oldPaths.length; i++) {
                        if (oldPaths[i] !== newPaths[i]) return true;
                    }

                    return false;
                },

                markUserInitiatedProjectSwitch: (projectId: string) => {
                    console.log(`🔒 [IDE] Marking user-initiated switch to ${projectId}`);
                    set({
                        projectSwitchProtection: {
                            lastUserSwitchTime: Date.now(),
                            lastUserSwitchProjectId: projectId,
                            isLocked: true
                        }
                    });

                    setTimeout(() => {
                        const currentState = get();
                        if (currentState.projectSwitchProtection.lastUserSwitchProjectId === projectId) {
                            set({
                                projectSwitchProtection: {
                                    ...currentState.projectSwitchProtection,
                                    isLocked: false
                                }
                            });
                            console.log(`🔓 [IDE] Auto-unlocked project switching for ${projectId}`);
                        }
                    }, 5000);
                },
            })
        )
    )
);

// ==============================================
// ADD THE DEPLOYMENT TRACKING HELPER FUNCTIONS
// ==============================================
const trackDeploymentSuccess = async (targetType: string) => {
    try {
        const state = useIDEStore.getState();
        const platformActor = state.platformActor;

        console.log('Platform actor exists:', !!platformActor);
        console.log('Platform actor methods:', Object.keys(platformActor || {}));

        if (!platformActor) return;

        // Get current deployment counters
        const deploymentCounters = await platformActor.getDeploymentCounters();

        // Increment total deployments
        const newTotalDeployments = deploymentCounters.totalDeployments + BigInt(1);
        await platformActor.updateDeploymentCounter("totalDeployments", newTotalDeployments);

        // Increment successful deployments
        const newSuccessfulDeployments = deploymentCounters.successfulDeployments + BigInt(1);
        await platformActor.updateDeploymentCounter("successfulDeployments", newSuccessfulDeployments);

        // Track daily deployments
        const today = new Date().toISOString().split('T')[0];
        const dailyDeployments = await platformActor.getDailyDeployments();
        const todayCount = dailyDeployments.find(d => d.date === today);
        const newDailyCount = todayCount ? todayCount.count + BigInt(1) : BigInt(1);
        await platformActor.addDailyDeployment(today, newDailyCount);

        // Track deployments by target
        const deploymentsByTarget = await platformActor.getDeploymentsByTarget();
        const targetCount = deploymentsByTarget.find(t => t[0] === targetType);
        const newTargetCount = targetCount ? targetCount[1] + BigInt(1) : BigInt(1);
        await platformActor.updateDeploymentTarget(targetType, newTargetCount);

        console.log(`[Analytics] Tracked successful deployment: ${targetType}`);
    } catch (analyticsError) {
        console.error('[Analytics] Error tracking deployment success:', analyticsError);
    }
};

const trackDeploymentFailure = async (targetType: string) => {
    try {
        const state = useIDEStore.getState();
        const platformActor = state.platformActor;

        console.log('Platform actor exists:', !!platformActor);
        console.log('Platform actor methods:', Object.keys(platformActor || {}));

        if (!platformActor) return;

        // Get current deployment counters
        const deploymentCounters = await platformActor.getDeploymentCounters();

        // Increment total deployments
        const newTotalDeployments = deploymentCounters.totalDeployments + BigInt(1);
        await platformActor.updateDeploymentCounter("totalDeployments", newTotalDeployments);

        // Increment failed deployments
        const newFailedDeployments = deploymentCounters.failedDeployments + BigInt(1);
        await platformActor.updateDeploymentCounter("failedDeployments", newFailedDeployments);

        console.log(`[Analytics] Tracked failed deployment: ${targetType}`);
    } catch (analyticsError) {
        console.error('[Analytics] Error tracking deployment failure:', analyticsError);
    }
};

// Helper function for determining MIME type
const getMimeType = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    switch (ext) {
        case 'html':
            return 'text/html';
        case 'css':
            return 'text/css';
        case 'json':
            return 'application/json';
        default:
            return 'text/plain';
    }
};

// Helper function to get language from filename
const getLanguageFromFileName = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    switch (extension) {
        case 'mo':
            return 'motoko';
        case 'ts':
        case 'tsx':
            return 'typescript';
        case 'js':
        case 'jsx':
            return 'javascript';
        case 'css':
            return 'css';
        case 'html':
            return 'html';
        case 'json':
            return 'json';
        default:
            return 'plaintext';
    }
};

const formatVersion = (version: SemanticVersion): string => {
    let result = `${version.major}.${version.minor}.${version.patch}`;
    if (version.prerelease) result += `-${version.prerelease}`;
    if (version.build) result += `+${version.build}`;
    return result;
};


// Enhanced transformFiles function that specifically handles the version file structure
const transformFiles = (files) => {
    return files.map(file => {
        try {
            // If the file already has a name and properly structured content, use it
            if (file.name && (typeof file.content === 'string' || !file.content)) {
                return file;
            }

            // Create a new file object to modify
            let newFile = {...file};

            // 1. Handle missing name property
            if (!newFile.name) {
                // CASE 1: Handle version-specific file structure with project ID in the file ID
                // Example: "project-m98rcpza-y1pb5/USER_CANISTER/src/backend/main.mo"
                if (newFile.id && typeof newFile.id === 'string' && newFile.id.includes('/')) {
                    // For IDs that don't have a colon but have the project ID at the start
                    if (!newFile.id.includes(':')) {
                        // Extract the file path, removing the project ID prefix
                        let filePath = newFile.id;
                        // Check for project ID prefix pattern (e.g., "project-xxx/")
                        const projectIdMatch = newFile.id.match(/^(project-[^\/]+)\//);
                        if (projectIdMatch) {
                            // Remove the project ID prefix
                            filePath = newFile.id.substring(projectIdMatch[0].length);
                        }

                        console.log(`[INFO] Created name from version file id: ${filePath}`);
                        newFile.name = filePath;
                    }
                        // CASE 2: Handle regular file ID with colon
                    // Example: "project-m98rcpza-y1pb5:USER_CANISTER/src/backend/main.mo"
                    else {
                        let idParts = newFile.id.split(':');
                        let filePath = idParts.length > 1 ? idParts[1] : newFile.id;
                        filePath = filePath.replace(/\/\//g, '/');

                        console.log(`[INFO] Created name from id with colon: ${filePath}`);
                        newFile.name = filePath;
                    }
                }
                // CASE 3: Handle path + filename combination
                else if (newFile.path && (newFile.fileName || newFile.basename)) {
                    const fileName = newFile.fileName || newFile.basename;
                    const cleanPath = newFile.path.endsWith('/') ? newFile.path : `${newFile.path}/`;
                    const fullPath = `${cleanPath}${fileName}`;

                    console.log(`[INFO] Created name from path+filename: ${fullPath}`);
                    newFile.name = fullPath;
                }
                // CASE 4: Use path as name if available
                else if (newFile.path) {
                    console.log(`[INFO] Using path as name: ${newFile.path}`);
                    newFile.name = newFile.path;
                }
                // CASE 5: No useful information for name
                else {
                    console.warn(`[WARN] Could not create name for file:`, newFile);
                    // Create a fallback name to prevent filtering
                    newFile.name = `unknown-file-${Math.random().toString(36).substring(2, 10)}`;
                }
            }

            // 2. Handle content structure
            if (newFile.content) {
                if (Array.isArray(newFile.content)) {
                    // Handle array content (typical in version responses)
                    if (newFile.content.length > 0) {
                        const contentItem = newFile.content[0];

                        if (contentItem && typeof contentItem === 'object') {
                            // Extract Text or Binary content from the object
                            if (contentItem.Text !== undefined) {
                                console.log(`[INFO] Extracted Text content for ${newFile.name}`);
                                newFile.content = contentItem.Text;
                            } else if (contentItem.Binary !== undefined) {
                                // For binary content, we can't display it directly in Monaco
                                console.log(`[INFO] Found Binary content for ${newFile.name}, using placeholder`);
                                newFile.content = "/* Binary content - cannot be displayed in editor */";
                            } else {
                                console.warn(`[WARN] Unknown content structure for ${newFile.name}, using empty string`);
                                newFile.content = "";
                            }
                        } else if (typeof contentItem === 'string') {
                            console.log(`[INFO] Using string content from array for ${newFile.name}`);
                            newFile.content = contentItem;
                        } else {
                            console.warn(`[WARN] Unhandled content type in array for ${newFile.name}, using empty string`);
                            newFile.content = "";
                        }
                    } else {
                        // Empty array, use empty string
                        newFile.content = "";
                    }
                } else if (typeof newFile.content === 'object' && newFile.content !== null) {
                    // Handle object content (sometimes direct objects without arrays)
                    if (newFile.content.Text !== undefined) {
                        newFile.content = newFile.content.Text;
                    } else if (newFile.content.Binary !== undefined) {
                        newFile.content = "/* Binary content - cannot be displayed in editor */";
                    } else {
                        // Unknown object structure
                        console.warn(`[WARN] Unknown content object structure for ${newFile.name}`, newFile.content);
                        newFile.content = "";
                    }
                }
                // String content is already properly formatted
            } else {
                // No content, initialize with empty string
                newFile.content = "";
            }

            return newFile;
        } catch (error) {
            console.error(`[ERROR] Error transforming file:`, file, error);
            // Return a minimal valid file
            return {
                ...file,
                name: file.name || (file.id ? String(file.id) : "unknown-file"),
                content: ""
            };
        }
    });
};

export const enhancedTransformFiles = (files) => {
    console.log('[PATH ANALYSIS] Original server files:', files.map((f, i) => `File ${i}: ${JSON.stringify({
        path: f.path,
        fileName: f.fileName,
        fullPath: f.path && f.fileName ? `${f.path}/${f.fileName}` : undefined
    })}`));

    return files.map(file => {
        try {
            // Skip undefined files
            if (!file) {
                console.warn('[IDEStore] Undefined file in transformFiles');
                return {
                    name: `unknown-file-${Date.now()}`,
                    content: '',
                    fullId: ''
                };
            }

            // Use the existing backend fields exactly as they are
            const {id, path, fileName, fullPath, content} = file;

            // Determine display name for UI
            let name = '';

            // First priority: Use fullPath if provided by backend
            if (fullPath) {
                name = fullPath;
            }
            // Second priority: Construct from path + fileName
            else if (path && fileName) {
                name = `${path}/${fileName}`;
            }
            // Third priority: Extract from id if it has a project:path format
            else if (id && typeof id === 'string') {
                if (id.includes(':')) {
                    name = id.split(':')[1];
                } else if (id.includes('/')) {
                    const projectIdMatch = id.match(/^(project-[^\/]+)\//);
                    if (projectIdMatch) {
                        name = id.substring(projectIdMatch[0].length);
                    } else {
                        name = id;
                    }
                } else {
                    name = id;
                }
            }

            // Final fallback
            if (!name) {
                name = `unknown-file-${Date.now()}`;
                console.warn('[IDEStore] Could not determine file path for:', file);
            }

            // Extract content for editor display
            let extractedContent = '';

            if (typeof content === 'string') {
                extractedContent = content;
            } else if (Array.isArray(content) && content.length > 0) {
                const contentItem = content[0];
                if (contentItem && typeof contentItem === 'object') {
                    if ('Text' in contentItem) {
                        extractedContent = contentItem.Text;
                    } else if ('Binary' in contentItem) {
                        extractedContent = '/* Binary content - cannot be displayed in editor */';
                    }
                } else if (typeof contentItem === 'string') {
                    extractedContent = contentItem;
                }
            } else if (content && typeof content === 'object') {
                if ('Text' in content) {
                    extractedContent = content.Text;
                } else if ('Binary' in content) {
                    extractedContent = '/* Binary content - cannot be displayed in editor */';
                }
            }

            // Return all original fields plus the display fields
            return {
                ...file,                // Keep ALL original backend fields
                name,                   // Name for UI display
                content: extractedContent, // Content for editor
                fullId: id || '',      // ID for reference
                // We don't need to add originalPath or originalFileName since we're preserving all original fields
            };
        } catch (error) {
            console.error('[IDEStore] Error transforming file:', error, file);
            return {
                name: `error-file-${Date.now()}`,
                content: '',
                fullId: ''
            };
        }
    }).filter(Boolean); // Remove any null entries
};

function parseDependenciesFromToml(tomlContent: string): Record<string, string> {
    const dependencies: Record<string, string> = {};
    let inDependenciesSection = false;

    // Simple line-by-line parser
    const lines = tomlContent.split('\n');

    for (const line of lines) {
        const trimmedLine = line.trim();

        if (trimmedLine === '[dependencies]') {
            inDependenciesSection = true;
            continue;
        } else if (trimmedLine.startsWith('[') && trimmedLine.endsWith(']')) {
            inDependenciesSection = false;
            continue;
        }

        if (inDependenciesSection && trimmedLine && !trimmedLine.startsWith('#')) {
            const match = trimmedLine.match(/^([^=]+)\s*=\s*["']([^"']+)["']/);
            if (match) {
                const name = match[1].trim();
                const version = match[2].trim();
                dependencies[name] = version;
            }
        }
    }

    return dependencies;
}

export const initializeStore = (dependencies: {
    userActor: any;
    platformActor?: any;
    identity: any;
    userPrincipal: Principal | null;
    userCanisters: CanisterInfo[];
    onError: (error: string | null) => void;
    onSuccess: (message: string | null) => void;
    onProjectUpdate?: () => Promise<void>;
    activeTab?: string;
}) => {
    console.log('[IDEStore] Starting store initialization');

    // Create a debounced version of the project update function
    const debouncedProjectUpdate = createDebouncedProjectUpdate(
        dependencies.onProjectUpdate || null,
        500
    );

    const safeProjectUpdate = createSafeProjectUpdate(
        dependencies.onProjectUpdate || null,
        useIDEStore
    );

    // ENHANCED: Load AI rules from localStorage
    try {
        const savedRules = localStorage.getItem('ide-ai-rules');
        if (savedRules) {
            const parsedRules = JSON.parse(savedRules);
            useIDEStore.setState({aiRules: parsedRules});
            console.log('[IDEStore] Loaded AI rules from localStorage:', parsedRules.length);
        } else {
            // Initialize with default rules if none exist
            const defaultRules: AIRule[] = [
                {
                    id: 'default-security',
                    name: 'ICP Security Best Practices',
                    description: 'Follow ICP-specific security guidelines',
                    content: 'Always validate caller identity, use proper access controls, avoid exposing sensitive data in public methods, and implement rate limiting for expensive operations.',
                    type: 'template',
                    enabled: true,
                    category: 'security',
                    lastUpdated: Date.now(),
                    status: 'active'
                },
                {
                    id: 'default-style',
                    name: 'Motoko Style Guide',
                    description: 'Follow Motoko coding conventions',
                    content: 'Use camelCase for functions and variables, PascalCase for types, meaningful variable names, proper indentation, and clear documentation for public methods.',
                    type: 'template',
                    enabled: true,
                    category: 'style',
                    lastUpdated: Date.now(),
                    status: 'active'
                },
                {
                    id: 'default-performance',
                    name: 'Performance Optimization',
                    description: 'Optimize for cycles and memory usage',
                    content: 'Minimize computation in query functions, use stable variables appropriately, batch operations when possible, and avoid unnecessary data copying.',
                    type: 'template',
                    enabled: true,
                    category: 'performance',
                    lastUpdated: Date.now(),
                    status: 'active'
                }
            ];
            useIDEStore.setState({aiRules: defaultRules});
            console.log('[IDEStore] Initialized with default AI rules');
        }
    } catch (error) {
        console.warn('[IDEStore] Failed to load AI rules from localStorage:', error);
        // Initialize with empty array if loading fails
        useIDEStore.setState({aiRules: []});
    }

    // Initialize store with the dependencies (your existing code)
    useIDEStore.setState({
        userActor: dependencies.userActor,
        platformActor: dependencies.platformActor,
        identity: dependencies.identity,
        userPrincipal: dependencies.userPrincipal,
        userCanisters: dependencies.userCanisters || [],
        onError: dependencies.onError,
        onSuccess: dependencies.onSuccess,
        onProjectUpdate: dependencies.onProjectUpdate || null,
        debouncedProjectUpdate,
        safeProjectUpdate,
        originalProjectUpdate: dependencies.onProjectUpdate || null,
        diagnosticsTimeout: null,
        githubSources: []
    });

    console.log('[IDEStore] Initialized with dependencies and AI rules');

    // Return cleanup function
    return () => {
        const state = useIDEStore.getState();
        if (state.diagnosticsTimeout) {
            console.log('[IDEStore] Cleaning up diagnostics timeout');
            clearTimeout(state.diagnosticsTimeout);
            useIDEStore.setState({diagnosticsTimeout: null});
        }
    };
};

export default useIDEStore;